import i18n from "@/i18n";
// API base URL resolution:
//  • VITE_API_URL="" (empty string)  → relative URLs, correct for Vercel (same domain)
//  • VITE_API_URL not set + running on localhost → http://localhost:3001 (local dev)
//  • VITE_API_URL not set + running on any other host → "" (relative, same domain)
// This prevents the localhost fallback from leaking into production when the env
// var is simply absent from the Vercel project settings.
const _envApiUrl = import.meta.env.VITE_API_URL;
export const API_BASE: string =
  _envApiUrl !== undefined
    ? _envApiUrl
    : typeof window !== "undefined" && window.location.hostname === "localhost"
      ? "http://localhost:3001"
      : "";

// ── Tenant bootstrap (runs at module load, before any React renders) ───────────
// If the URL contains ?tenant=xxx, store it immediately so that getTenantSlug()
// returns the correct value from the very first component render.  This also
// means that navigating to /login?tenant=xxx while already logged-in as a
// different tenant will silently switch the active tenant key, causing
// AuthContext to initialise with the new tenant's localStorage slot (null →
// shows login page) rather than the old tenant's cached user.
{
  try {
    // ?tenant= param takes highest priority
    const tenantParam = new URLSearchParams(window.location.search).get("tenant");
    if (tenantParam?.trim()) {
      localStorage.setItem("besthr_active_tenant", tenantParam.trim().toLowerCase());
    } else {
      // Subdomain-based tenant: sync localStorage so all downstream reads
      // (tenantStorageKey, getTenantSlug) are consistent from the very first call.
      const _hostname = window.location.hostname;
      const _siteDomain = (import.meta.env.VITE_SITE_DOMAIN as string | undefined) || "portal.besthr.app";
      const _parts = _hostname.split(".");
      if (
        _hostname.endsWith(`.${_siteDomain}`) &&
        _parts.length >= 3 &&
        !["www", "api", "portal"].includes(_parts[0])
      ) {
        localStorage.setItem("besthr_active_tenant", _parts[0]);
      }
    }
  } catch { /* SSR / localStorage unavailable */ }
}

// Tenant-scoped localStorage key to prevent cross-tenant data leakage
export function tenantStorageKey(key: string): string {
  return `besthr_${getTenantSlug()}_${key}`;
}

function getCurrentUserId(): string | null {
  try {
    const stored = localStorage.getItem(tenantStorageKey("user"));
    if (stored) {
      const user = JSON.parse(stored);
      return user.id ?? null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function getTenantSlug(): string {
  // 1. Runtime override stored in localStorage (set by Login page via ?tenant= param)
  try {
    const override = localStorage.getItem("besthr_active_tenant");
    if (override && override.trim()) return override.trim();
  } catch { /* ignore */ }
  // 2. Read from subdomain — only for known production domain to avoid extracting
  //    Vercel preview hostnames (e.g. besthrapp-xyz.vercel.app) as tenant slugs.
  const hostname = window.location.hostname;
  const siteDomain = (import.meta.env.VITE_SITE_DOMAIN as string | undefined) || "portal.besthr.app";
  const parts = hostname.split(".");
  if (
    hostname.endsWith(`.${siteDomain}`) &&
    parts.length >= 3 &&
    parts[0] !== "www" && parts[0] !== "api" && parts[0] !== "portal"
  ) {
    return parts[0];
  }
  // 3. Explicit env override (local dev / CI)
  if (import.meta.env.VITE_TENANT) return import.meta.env.VITE_TENANT as string;
  // 4. Localhost fallback
  return "public";
}

function getStoredToken(): string | null {
  try {
    return localStorage.getItem(tenantStorageKey("token"));
  } catch {
    return null;
  }
}

function getActiveImpersonationToken(): string | null {
  try {
    const impStored = localStorage.getItem("besthr_sa_impersonation");
    if (impStored) {
      const imp = JSON.parse(impStored);
      if (imp.token && new Date(imp.expiresAt) > new Date()) {
        return imp.token;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

function ensureWritableSession(method: string, impToken: string | null) {
  if (impToken && method !== "GET") {
    throw new ApiError(403, "Read-only: write operations are disabled during impersonation preview");
  }
}

async function parseErrorResponse(res: Response): Promise<ApiError> {
  const body = await res.json().catch(() => ({}));
  let message =
    body.error ||
    body.message ||
    `Request failed (${res.status})`;
  let code = body.code;
  if (code === "common.moduleDisabled" || message === "Module not enabled for this tenant") {
    code = "common.moduleDisabled";
    message = i18n.t(code);
  } else if (typeof code === "string" && code.startsWith("reporting.")) {
    message = i18n.t(code);
  }

  if (res.status === 413 && !body.error) {
    message = "The selected file is too large. Document files must be 10 MB or smaller.";
    code = "DOCUMENT_FILE_TOO_LARGE";
  }

  if (res.status === 504 && !body.error) {
    message = "The server took too long to respond. Please try again.";
  }

  return new ApiError(res.status, message, code, body);
}

async function apiMultipartFetch<T>(
  path: string,
  formData: FormData,
  options?: { method?: string },
): Promise<T> {
  const method = (options?.method ?? "POST").toUpperCase();
  const token = getStoredToken();
  const tenantSlug = getTenantSlug();
  const impToken = getActiveImpersonationToken();

  ensureWritableSession(method, impToken);

  const sep = path.includes("?") ? "&" : "?";
  const cacheSafePath = `${path}${sep}_t=${encodeURIComponent(tenantSlug)}`;

  const res = await fetch(`${API_BASE}${cacheSafePath}`, {
    method,
    cache: "no-store" as RequestCache,
    headers: {
      "x-tenant": tenantSlug,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(impToken ? { "x-impersonation-token": impToken } : {}),
    },
    body: formData,
  });

  if (!res.ok) {
    throw await parseErrorResponse(res);
  }

  return res.json();
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const tenantSlug = getTenantSlug();
  const impToken = getActiveImpersonationToken();

  // Block all non-GET requests during impersonation — read-only mode.
  // This is the frontend safety net; the server also blocks writes.
  const method = (options?.method ?? "GET").toUpperCase();
  ensureWritableSession(method, impToken);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "x-tenant": tenantSlug,
    // Always send the JWT when available — it's the universal auth mechanism.
    // When impersonating, ALSO send the impersonation token so the server can
    // enable write-blocking and audit logging. If the server-side impersonation
    // handler isn't deployed yet or the token expires, the JWT still works.
    ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    ...(impToken ? { "x-impersonation-token": impToken } : {}),
  };

  // Append tenant slug to URL as a cache-busting query parameter.
  // Browser HTTP cache keys on URL only (not headers), so
  // /api/surveys?_t=aimms and /api/surveys?_t=public are cached separately.
  const sep = path.includes("?") ? "&" : "?";
  const cacheSafePath = `${path}${sep}_t=${encodeURIComponent(tenantSlug)}`;

  const res = await fetch(`${API_BASE}${cacheSafePath}`, {
    ...options,
    cache: "no-store" as RequestCache,
    headers: {
      ...headers,
      ...((options?.headers as Record<string, string>) || {}),
    },
  });

  if (!res.ok) {
    if (res.status === 401) {
      // Only treat a 401 as a session expiry when we actually SENT a token that
      // the server rejected. A tokenless 401 is expected (e.g. background queries
      // firing on the login / SSO-callback pages before a session exists) and
      // must NOT dispatch session-expired — doing so can tear down a freshly
      // established session during the SSO redirect race.
      const hadToken = !!token;
      // Don't fire session-expired if we're in an active impersonation session —
      // the impersonation token itself is valid; the 401 likely comes from a
      // secondary API call that doesn't participate in impersonation flow.
      const isImpersonating = !!impToken;
      if (hadToken && !isImpersonating) {
        // Dispatch session-expired event so AuthContext can clear state and
        // redirect to login. Must not throw before dispatching.
        window.dispatchEvent(new CustomEvent("besthr:session-expired"));
      }
    }
    throw await parseErrorResponse(res);
  }

  const data = await res.json();
  return data as T;
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const encodedMatch = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return encodedMatch[1].trim().replace(/^"|"$/g, "");
    }
  }

  const quotedMatch = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const plainMatch = header.match(/filename\s*=\s*([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function apiDownloadBlob(path: string, fallbackFilename: string): Promise<void> {
  const response = await apiFetchBlob(path);
  const filename = response.filename ?? fallbackFilename;
  triggerBlobDownload(response.blob, filename);
}

async function apiFetchBlob(path: string): Promise<{
  blob: Blob;
  filename: string | null;
}> {
  const token = getStoredToken();
  const tenantSlug = getTenantSlug();
  const impToken = getActiveImpersonationToken();

  ensureWritableSession("GET", impToken);

  const sep = path.includes("?") ? "&" : "?";
  const cacheSafePath = `${path}${sep}_t=${encodeURIComponent(tenantSlug)}`;

  const res = await fetch(`${API_BASE}${cacheSafePath}`, {
    cache: "no-store" as RequestCache,
    headers: {
      "x-tenant": tenantSlug,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(impToken ? { "x-impersonation-token": impToken } : {}),
    },
  });

  if (!res.ok) {
    if (res.status === 401 && !impToken) {
      window.dispatchEvent(new CustomEvent("besthr:session-expired"));
    }
    throw await parseErrorResponse(res);
  }

  return {
    blob: await res.blob(),
    filename: filenameFromContentDisposition(res.headers.get("Content-Disposition")),
  };
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.name = "ApiError";
  }
}

// ---- Tenant Config ----

export interface TenantConfig {
  enabledModules: Record<string, boolean> | null;
  companyCountry: string | null;
}

export async function apiGetTenantConfig(): Promise<TenantConfig> {
  return apiFetch<TenantConfig>("/api/tenant-config");
}

// ---- Auth ----

import type { AppUser } from "@/contexts/AuthContext";

export async function apiLogin(
  email: string,
  password: string,
): Promise<{ user: AppUser; token: string } | { requires2FA: true; tempToken: string }> {
  const result = await apiFetch<{ user?: AppUser; token?: string; tenantSlug?: string; requires2FA?: boolean; tempToken?: string }>(
    "/api/auth/login",
    { method: "POST", body: JSON.stringify({ email, password }) },
  );
  if (result.requires2FA && result.tempToken) {
    return { requires2FA: true, tempToken: result.tempToken };
  }
  // Persist the tenant that authenticated this user so all subsequent API
  // calls use the correct schema (survives navigation within the SPA session).
  if (result.tenantSlug) {
    localStorage.setItem("besthr_active_tenant", result.tenantSlug);
  }
  // Store the JWT for use in Authorization: Bearer header
  if (result.token) {
    localStorage.setItem(tenantStorageKey("token"), result.token);
  }
  return result as { user: AppUser; token: string };
}

export async function apiVerify2FA(
  tempToken: string,
  code: string,
): Promise<{ user: AppUser; token: string; tenantSlug: string }> {
  const result = await apiFetch<{ user: AppUser; token: string; tenantSlug: string }>(
    "/api/auth/2fa/verify",
    { method: "POST", body: JSON.stringify({ tempToken, code }) },
  );
  if (result.tenantSlug) {
    localStorage.setItem("besthr_active_tenant", result.tenantSlug);
  }
  if (result.token) {
    localStorage.setItem(tenantStorageKey("token"), result.token);
  }
  return result;
}

export async function apiForgotPassword(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>("/api/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function apiResetPassword(
  token: string,
  newPassword: string,
): Promise<{ success: true }> {
  return apiFetch<{ success: true }>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });
}

export async function apiLogout(): Promise<void> {
  await apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
  // Clear the JWT token from storage on logout
  try { localStorage.removeItem(tenantStorageKey("token")); } catch { /* ignore */ }
}


// ---- Employees ----

import type { EmployeeProfile } from "@/data/employees";
import type { FlatEmployeeRow } from "@/lib/analytics/fieldCatalog";

export type EmployeeListItem = EmployeeProfile & { directReportsCount: number; subRole?: string | null };

export async function apiGetEmployees(): Promise<EmployeeListItem[]> {
  return apiFetch("/api/employees");
}

export async function apiGetEmployee(id: string): Promise<EmployeeProfile> {
  return apiFetch(`/api/employees/${id}`);
}

export interface CelebrationItem {
  id: number;
  name: string;
  initials: string;
  avatarColor: string;
  avatarUrl: string | null;
  eventType: "birthday" | "anniversary";
  years?: number;
  daysAway: number;
}

export async function apiGetCelebrations(): Promise<CelebrationItem[]> {
  return apiFetch("/api/employees/celebrations");
}

export interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  endDate: string | null;
  category: string;
  description: string | null;
  allDay: boolean;
  time: string | null;
  location: string | null;
  createdBy: string | null;
  coverImageUrl: string | null;
  organizer: string | null;
  link: string | null;
}

export async function apiGetCalendarEvents(): Promise<CalendarEvent[]> {
  return apiFetch("/api/corporate-calendar");
}

export async function apiGetCalendarEvent(id: string): Promise<CalendarEvent> {
  return apiFetch(`/api/corporate-calendar/${id}`);
}

export async function apiUploadEventCoverImage(id: string, file: File): Promise<CalendarEvent> {
  const formData = new FormData();
  formData.append("image", file);
  return apiMultipartFetch<CalendarEvent>(`/api/corporate-calendar/${id}/cover-image`, formData);
}

export async function apiCreateCalendarEvent(data: Omit<CalendarEvent, "id">): Promise<CalendarEvent> {
  return apiFetch("/api/corporate-calendar", { method: "POST", body: JSON.stringify(data) });
}

export async function apiUpdateCalendarEvent(id: string, data: Omit<CalendarEvent, "id">): Promise<CalendarEvent> {
  return apiFetch(`/api/corporate-calendar/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function apiDeleteCalendarEvent(id: string): Promise<void> {
  return apiFetch(`/api/corporate-calendar/${id}`, { method: "DELETE" });
}

export async function apiGetEmployeeByEmail(
  email: string,
): Promise<EmployeeProfile> {
  return apiFetch(`/api/employees/by-email/${encodeURIComponent(email)}`);
}

export interface UpdateEmployeePayload {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  birthday?: string;
  nationality?: string;
  gender?: string;
  jobTitle?: string;
  department?: string;
  team?: string;
  location?: string;
  company?: string;
  employeeId?: string;
  status?: string;
  contractType?: string;
  workSchedule?: string;
  startDate?: string;
  reportsToId?: string | null;
  isLineManager?: boolean;
  fullProfileAccess?: boolean;
}

export async function apiUpdateEmployee(
  id: string,
  data: UpdateEmployeePayload,
): Promise<EmployeeProfile> {
  return apiFetch(`/api/employees/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiUploadAvatar(
  employeeId: string,
  file: File,
): Promise<EmployeeProfile> {
  const formData = new FormData();
  formData.append("avatar", file);
  return apiMultipartFetch<EmployeeProfile>(`/api/employees/${employeeId}/avatar`, formData);
}

export async function apiDeleteAvatar(
  employeeId: string,
): Promise<EmployeeProfile> {
  return apiFetch(`/api/employees/${employeeId}/avatar`, { method: "DELETE" });
}

// ---- Field Permissions ----

export interface FieldPermission {
  id: number;
  fieldName: string;
  section: string;
  editableByUser: boolean;
  editableByHr: boolean;
  editableByLineManager: boolean;
  editableByAdmin: boolean;
}

export async function apiGetFieldPermissions(): Promise<FieldPermission[]> {
  return apiFetch("/api/field-permissions");
}

export interface UpdateFieldPermissionPayload {
  fieldName: string;
  editableByUser: boolean;
  editableByHr: boolean;
  editableByLineManager: boolean;
  editableByAdmin: boolean;
}

export async function apiUpdateFieldPermissions(
  data: UpdateFieldPermissionPayload[],
): Promise<FieldPermission[]> {
  return apiFetch("/api/field-permissions", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ---- Bank Details ----

export interface BankDetails {
  id: number;
  employeeId: string;
  bankName: string | null;
  iban: string | null;
  bicSwift: string | null;
  accountHolderName: string | null;
}

export async function apiGetBankDetails(
  empId: string,
): Promise<BankDetails | null> {
  return apiFetch(`/api/employees/${empId}/bank-details`);
}

export async function apiUpdateBankDetails(
  empId: string,
  data: Partial<Omit<BankDetails, "id" | "employeeId">>,
): Promise<BankDetails> {
  return apiFetch(`/api/employees/${empId}/bank-details`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ---- Emergency Contacts ----

export interface EmergencyContact {
  id: number;
  employeeId: string;
  contactName: string;
  relationship: string | null;
  phone: string | null;
  email: string | null;
}

export async function apiGetEmergencyContacts(
  empId: string,
): Promise<EmergencyContact[]> {
  return apiFetch(`/api/employees/${empId}/emergency-contacts`);
}

export async function apiCreateEmergencyContact(
  empId: string,
  data: Omit<EmergencyContact, "id" | "employeeId">,
): Promise<EmergencyContact> {
  return apiFetch(`/api/employees/${empId}/emergency-contacts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateEmergencyContact(
  empId: string,
  contactId: number,
  data: Partial<Omit<EmergencyContact, "id" | "employeeId">>,
): Promise<EmergencyContact> {
  return apiFetch(`/api/employees/${empId}/emergency-contacts/${contactId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteEmergencyContact(
  empId: string,
  contactId: number,
): Promise<void> {
  return apiFetch(`/api/employees/${empId}/emergency-contacts/${contactId}`, {
    method: "DELETE",
  });
}

// ---- Children ----

export interface EmployeeChild {
  id: number;
  employeeId: string;
  firstName: string;
  dateOfBirth: string;
}

export interface ChildrenSummaryGroup {
  label: string;
  count: number;
}

export interface ChildrenSummaryChild {
  id: number;
  firstName: string;
  dateOfBirth: string;
  employeeName: string;
  age: number;
}

export interface ChildrenSummary {
  totalChildren: number;
  byAgeGroup: ChildrenSummaryGroup[];
  children: ChildrenSummaryChild[];
}

export async function apiGetChildren(empId: string): Promise<EmployeeChild[]> {
  return apiFetch(`/api/employees/${empId}/children`);
}

export async function apiCreateChild(
  empId: string,
  data: Omit<EmployeeChild, "id" | "employeeId">,
): Promise<EmployeeChild> {
  return apiFetch(`/api/employees/${empId}/children`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateChild(
  empId: string,
  childId: number,
  data: Partial<Omit<EmployeeChild, "id" | "employeeId">>,
): Promise<EmployeeChild> {
  return apiFetch(`/api/employees/${empId}/children/${childId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteChild(empId: string, childId: number): Promise<void> {
  return apiFetch(`/api/employees/${empId}/children/${childId}`, {
    method: "DELETE",
  });
}

export async function apiGetChildrenSummary(minAge?: number, maxAge?: number): Promise<ChildrenSummary> {
  const params = new URLSearchParams();
  if (minAge != null) params.set("minAge", String(minAge));
  if (maxAge != null) params.set("maxAge", String(maxAge));
  const qs = params.toString();
  return apiFetch(`/api/organization/children-summary${qs ? `?${qs}` : ""}`);
}

// ---- Qualifications ----

export interface Qualification {
  id: number;
  employeeId: string;
  type: "education" | "certification" | "training";
  title: string;
  institution: string | null;
  dateObtained: string | null;
  expiryDate: string | null;
  recurrenceMonths?: number | null;
}

export async function apiGetQualifications(
  empId: string,
): Promise<Qualification[]> {
  return apiFetch(`/api/employees/${empId}/qualifications`);
}

export async function apiCreateQualification(
  empId: string,
  data: Omit<Qualification, "id" | "employeeId">,
): Promise<Qualification> {
  return apiFetch(`/api/employees/${empId}/qualifications`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateQualification(
  empId: string,
  qualId: number,
  data: Partial<Omit<Qualification, "id" | "employeeId">>,
): Promise<Qualification> {
  return apiFetch(`/api/employees/${empId}/qualifications/${qualId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteQualification(
  empId: string,
  qualId: number,
): Promise<void> {
  return apiFetch(`/api/employees/${empId}/qualifications/${qualId}`, {
    method: "DELETE",
  });
}

// ---- Certifications / Positions ----

export interface Position {
  id: number;
  title: string;
  description: string | null;
  requiredCertifications: { id: number; certificationName: string }[];
  requiredTrainings: { id: number; trainingName: string }[];
}

export interface TrainingType {
  id: number;
  name: string;
  description: string | null;
  isRecurring: boolean;
  recurrenceMonths: number | null;
}

export interface TrainingComplianceEmployee {
  complianceDataAvailable?: boolean;
  employeeId: string;
  employeeName: string;
  avatarUrl: string | null;
  avatarColor: string | null;
  initials: string;
  department: string;
  positionId: number | null;
  positionTitle: string | null;
  requiredTrainings: string[];
  heldTrainings: { title: string; completedDate: string | null; expiryDate: string | null; isRecurring: boolean }[];
  missingTrainings: string[];
  expiringTrainings: { title: string; expiryDate: string }[];
  expiredTrainings: { title: string; expiryDate: string }[];
}

export interface CertComplianceEmployee {
  complianceDataAvailable?: boolean;
  employeeId: string;
  employeeName: string;
  avatarUrl: string | null;
  avatarColor: string | null;
  initials: string;
  department: string;
  positionId: number | null;
  positionTitle: string | null;
  requiredCerts: string[];
  heldCerts: { title: string; expiryDate: string | null }[];
  missingCerts: string[];
  expiringCerts: { title: string; expiryDate: string }[];
  expiredCerts: { title: string; expiryDate: string }[];
}

export async function apiGetPositions(): Promise<Position[]> {
  return apiFetch("/api/certifications/positions");
}

export async function apiCreatePosition(data: { title: string; description?: string }): Promise<Position> {
  return apiFetch("/api/certifications/positions", { method: "POST", body: JSON.stringify(data) });
}

export async function apiUpdatePosition(id: number, data: Partial<{ title: string; description: string }>): Promise<Position> {
  return apiFetch(`/api/certifications/positions/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function apiDeletePosition(id: number): Promise<void> {
  return apiFetch(`/api/certifications/positions/${id}`, { method: "DELETE" });
}

export async function apiAddPositionRequirement(posId: number, certificationName: string): Promise<{ id: number; certificationName: string }> {
  return apiFetch(`/api/certifications/positions/${posId}/requirements`, {
    method: "POST",
    body: JSON.stringify({ certificationName }),
  });
}

export async function apiRemovePositionRequirement(posId: number, reqId: number): Promise<void> {
  return apiFetch(`/api/certifications/positions/${posId}/requirements/${reqId}`, { method: "DELETE" });
}

export async function apiAssignEmployeePosition(empId: string, positionId: number | null): Promise<void> {
  return apiFetch(`/api/certifications/employees/${empId}/position`, {
    method: "PUT",
    body: JSON.stringify({ positionId }),
  });
}

export async function apiGetCertificationOverview(): Promise<CertComplianceEmployee[]> {
  return apiFetch("/api/certifications/overview");
}

export interface CertificateType {
  id: number;
  name: string;
  description: string | null;
}

export async function apiGetCertificateTypes(): Promise<CertificateType[]> {
  return apiFetch("/api/certifications/certificate-types");
}

export async function apiCreateCertificateType(data: { name: string; description?: string }): Promise<CertificateType> {
  return apiFetch("/api/certifications/certificate-types", { method: "POST", body: JSON.stringify(data) });
}

export async function apiDeleteCertificateType(id: number): Promise<void> {
  return apiFetch(`/api/certifications/certificate-types/${id}`, { method: "DELETE" });
}

export async function apiGetTrainingTypes(): Promise<TrainingType[]> {
  return apiFetch("/api/certifications/training-types");
}

export async function apiCreateTrainingType(data: { name: string; description?: string; isRecurring: boolean; recurrenceMonths?: number }): Promise<TrainingType> {
  return apiFetch("/api/certifications/training-types", { method: "POST", body: JSON.stringify(data) });
}

export async function apiDeleteTrainingType(id: number): Promise<void> {
  return apiFetch(`/api/certifications/training-types/${id}`, { method: "DELETE" });
}

export async function apiAddPositionTrainingRequirement(posId: number, trainingName: string): Promise<{ id: number; trainingName: string }> {
  return apiFetch(`/api/certifications/positions/${posId}/training-requirements`, {
    method: "POST",
    body: JSON.stringify({ trainingName }),
  });
}

export async function apiRemovePositionTrainingRequirement(posId: number, reqId: number): Promise<void> {
  return apiFetch(`/api/certifications/positions/${posId}/training-requirements/${reqId}`, { method: "DELETE" });
}

export async function apiGetTrainingOverview(): Promise<TrainingComplianceEmployee[]> {
  return apiFetch("/api/certifications/training-overview");
}

// ---- Equipment ----

export type EquipmentStatus = "Active" | "Expired" | "Returned" | "Lost";

export interface Equipment {
  id: number;
  stockId?: number | null;
  employeeId: string | null;
  employeeName?: string | null;
  itemName: string;
  serialNumber: string | null;
  inventoryNumber?: string | null;
  besthrNumber?: string | null;
  externalId?: string | null;
  category?: string | null;
  assignedDate: string | null;
  returnDate: string | null;
  expiryDate?: string | null;
  status: EquipmentStatus;
  isExpired?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EquipmentStock {
  id: number;
  name: string;
  category: string | null;
  externalId: string | null;
  totalQuantity: number;
  assignedQuantity: number;
  employeeAssignedQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EquipmentFilters {
  employeeId?: string | number;
  expiringSoonDays?: number;
  unassigned?: boolean;
}

export type EquipmentPayload = {
  employeeId?: string | number | null;
  itemName: string;
  serialNumber?: string | null;
  inventoryNumber?: string | null;
  externalId?: string | null;
  category?: string | null;
  assignedDate?: string | null;
  returnDate?: string | null;
  expiryDate?: string | null;
  status?: EquipmentStatus;
};

export type EquipmentStockPayload = {
  name: string;
  category?: string | null;
  externalId?: string | null;
  totalQuantity?: number;
  assignedQuantity?: number;
  notes?: string | null;
};

function equipmentQuery(params?: EquipmentFilters): string {
  if (!params) return "";
  const entries = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => [key, String(value)]);
  const qs = new URLSearchParams(entries).toString();
  return qs ? `?${qs}` : "";
}

export async function apiGetEquipment(empId: string): Promise<Equipment[]>;
export async function apiGetEquipment(params?: EquipmentFilters): Promise<Equipment[]>;
export async function apiGetEquipment(arg?: string | EquipmentFilters): Promise<Equipment[]> {
  if (typeof arg === "string") return apiFetch(`/api/employees/${arg}/equipment`);
  return apiFetch(`/api/equipment${equipmentQuery(arg)}`);
}

export async function apiCreateEquipment(
  empId: string,
  data: Omit<Equipment, "id" | "employeeId">,
): Promise<Equipment>;
export async function apiCreateEquipment(data: EquipmentPayload): Promise<Equipment>;
export async function apiCreateEquipment(
  arg1: string | EquipmentPayload,
  arg2?: Omit<Equipment, "id" | "employeeId">,
): Promise<Equipment> {
  if (typeof arg1 !== "string") {
    return apiFetch("/api/equipment", {
      method: "POST",
      body: JSON.stringify(arg1),
    });
  }
  return apiFetch(`/api/employees/${arg1}/equipment`, {
    method: "POST",
    body: JSON.stringify(arg2),
  });
}

export async function apiUpdateEquipment(
  empId: string,
  equipId: number,
  data: Partial<Omit<Equipment, "id" | "employeeId">>,
): Promise<Equipment>;
export async function apiUpdateEquipment(
  equipId: number,
  data: Partial<EquipmentPayload>,
): Promise<Equipment>;
export async function apiUpdateEquipment(
  arg1: string | number,
  arg2: number | Partial<EquipmentPayload>,
  arg3?: Partial<Omit<Equipment, "id" | "employeeId">>,
): Promise<Equipment> {
  if (typeof arg1 === "number") {
    return apiFetch(`/api/equipment/${arg1}`, {
      method: "PUT",
      body: JSON.stringify(arg2),
    });
  }
  return apiFetch(`/api/employees/${arg1}/equipment/${arg2}`, {
    method: "PUT",
    body: JSON.stringify(arg3),
  });
}

export async function apiDeleteEquipment(
  empId: string,
  equipId: number,
): Promise<void>;
export async function apiDeleteEquipment(equipId: number): Promise<Equipment>;
export async function apiDeleteEquipment(arg1: string | number, arg2?: number): Promise<void | Equipment> {
  if (typeof arg1 === "number") {
    return apiFetch(`/api/equipment/${arg1}`, {
      method: "DELETE",
    });
  }
  return apiFetch(`/api/employees/${arg1}/equipment/${arg2}`, {
    method: "DELETE",
  });
}

export async function apiReassignEquipment(
  equipId: number,
  data: { employeeId?: string | number | null; assignedDate?: string | null },
): Promise<Equipment> {
  return apiFetch(`/api/equipment/${equipId}/reassign`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiGetEquipmentStock(): Promise<EquipmentStock[]> {
  return apiFetch("/api/equipment/stock");
}

export async function apiCreateEquipmentStock(data: EquipmentStockPayload): Promise<EquipmentStock> {
  return apiFetch("/api/equipment/stock", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateEquipmentStock(
  id: number,
  data: Partial<EquipmentStockPayload>,
): Promise<EquipmentStock> {
  return apiFetch(`/api/equipment/stock/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiAssignEquipmentStock(
  id: number,
  data: { employeeId: string | number; assignedDate: string; serialNumber?: string | null; inventoryNumber?: string | null },
): Promise<Equipment> {
  return apiFetch(`/api/equipment/stock/${id}/assign`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteEquipmentStock(id: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/equipment/stock/${id}`, {
    method: "DELETE",
  });
}

// ---- Visa ----

export interface Visa {
  id: number;
  employeeId: string;
  permitType: string | null;
  permitNumber: string | null;
  issuingCountry: string | null;
  issueDate: string | null;
  expiryDate: string | null;
}

export async function apiGetVisa(empId: string): Promise<Visa | null> {
  return apiFetch(`/api/employees/${empId}/visa`);
}

export async function apiUpdateVisa(
  empId: string,
  data: Partial<Omit<Visa, "id" | "employeeId">>,
): Promise<Visa> {
  return apiFetch(`/api/employees/${empId}/visa`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ---- Employment Details ----

export interface EmploymentDetails {
  contractEndDate?: string | null;
  noticePeriodDays?: number | null;
  workArrangement?: "on-site" | "hybrid" | "remote" | null;
  careerLevel?: string | null;
  costCenter?: string | null;
  benefitsEligible: boolean;
  probationEndDate?: string | null;
  probationPeriodMonths?: number | null;
  hoursPerWeek?: number | null;
  ftePercentage?: number | null;
  partTimeHoursPerDay?: number | null;
  partTimeIncludesSaturday?: boolean | null;
  overtimeEligible: boolean;
  contractorCompany?: string | null;
  scopeOfWork?: string | null;
  billingCycle?: string | null;
  ndaSigned: boolean;
  timeTrackingRequired: boolean;
  internshipType?: "paid" | "unpaid" | null;
  mentorId?: number | null;
  schoolUniversity?: string | null;
  academicProgram?: string | null;
  expectedGraduationDate?: string | null;
  isConversionEligible: boolean;
}

export interface EmploymentHistoryEntry {
  id: number;
  contractType: string;
  startDate: string | null;
  endDate: string | null;
  jobTitle: string | null;
  department: string | null;
  baseSalary: number | null;
  currency: string | null;
  changedByName: string | null;
  notes: string | null;
  createdAt: string;
}

export async function apiGetEmploymentDetails(empId: string): Promise<EmploymentDetails | null> {
  return apiFetch(`/api/employees/${empId}/employment-details`);
}

export async function apiUpdateEmploymentDetails(
  empId: string,
  data: Partial<EmploymentDetails>
): Promise<EmploymentDetails> {
  return apiFetch(`/api/employees/${empId}/employment-details`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function apiGetEmploymentHistory(empId: string): Promise<EmploymentHistoryEntry[]> {
  return apiFetch(`/api/employees/${empId}/employment-history`);
}

// ---- Salary ----

export interface Salary {
  id: number;
  employeeId: string;
  baseSalary: number | null;
  currency: string;
  payFrequency: string;
  bonus: number | null;
  effectiveDate: string | null;
}

export async function apiGetSalary(empId: string): Promise<Salary | null> {
  return apiFetch(`/api/employees/${empId}/salary`);
}

export async function apiUpdateSalary(
  empId: string,
  data: Partial<Omit<Salary, "id" | "employeeId">>,
): Promise<Salary> {
  return apiFetch(`/api/employees/${empId}/salary`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ---- Documents ----

export interface EmployeeDocument {
  id: number;
  employeeId: string;
  documentName: string;
  documentType: string | null;
  fileUrl: string | null;
  uploadedAt: string;
}

export async function apiGetDocuments(
  empId: string,
): Promise<EmployeeDocument[]> {
  return apiFetch(`/api/employees/${empId}/documents`);
}

export async function apiCreateDocument(
  empId: string,
  data: Omit<EmployeeDocument, "id" | "employeeId" | "uploadedAt">,
): Promise<EmployeeDocument> {
  return apiFetch(`/api/employees/${empId}/documents`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteDocument(
  empId: string,
  docId: number,
): Promise<void> {
  return apiFetch(`/api/employees/${empId}/documents/${docId}`, {
    method: "DELETE",
  });
}

// ---- Document Management (new /api/documents endpoint) ----

export type DocumentType =
  | "Contract"
  | "Annex"
  | "Bank Credit"
  | "Sick Leave"
  | "Visa"
  | "ID"
  | "Driving License"
  | "Passport"
  | "Criminal Record"
  | "Medical Check"
  | "Other";

export type DocumentStatus = "Pending" | "Signed" | "Completed" | "Rejected";

export interface HRDocument {
  id: number;
  employeeId: number;
  employeeName: string | null;
  documentName: string;
  documentType: DocumentType | null;
  status: DocumentStatus;
  fileKey: string | null;
  /** Storage metadata only. Protected files must be fetched through the authenticated API. */
  fileUrl: string | null;
  /** HTML content for documents generated from WYSIWYG templates (no file upload) */
  generatedContent: string | null;
  requestedBy: number | null;
  requestedByName: string | null;
  gdprExpiry: string | null;
  gdprExpired: boolean;
  rejectionReason: string | null;
  documentExpiry: string | null;
  uploadedAt: string;
  updatedAt: string;
}

export const DOCUMENT_TYPES: DocumentType[] = [
  "Contract",
  "Annex",
  "Bank Credit",
  "Sick Leave",
  "Visa",
  "ID",
  "Driving License",
  "Passport",
  "Criminal Record",
  "Medical Check",
  "Other",
];

/** Document types that carry an expiry date (passport, ID, driving licence) */
export const EXPIRY_DOCUMENT_TYPES: DocumentType[] = [
  "ID",
  "Driving License",
  "Passport",
];

export const DOCUMENT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
const DOCUMENT_UPLOAD_EXTENSIONS = new Set(["pdf", "doc", "docx", "jpg", "jpeg", "png"]);

export type DocumentUploadValidationCode =
  | "DOCUMENT_FILE_TOO_LARGE"
  | "DOCUMENT_FILE_TYPE_UNSUPPORTED"
  | "DOCUMENT_FILE_SIZE_INVALID";

export function validateDocumentUploadFile(file: File): DocumentUploadValidationCode | null {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!DOCUMENT_UPLOAD_EXTENSIONS.has(extension)) return "DOCUMENT_FILE_TYPE_UNSUPPORTED";
  if (!Number.isInteger(file.size) || file.size <= 0) return "DOCUMENT_FILE_SIZE_INVALID";
  if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) return "DOCUMENT_FILE_TOO_LARGE";
  return null;
}

export function getDocumentUploadErrorI18nKey(error: unknown): string | null {
  const uploadError = error as { code?: string; status?: number } | null;
  if (uploadError?.status === 413 || uploadError?.code === "DOCUMENT_FILE_TOO_LARGE") {
    return "documents.messages.fileTooLarge";
  }
  if (
    uploadError?.code === "DOCUMENT_FILE_TYPE_UNSUPPORTED" ||
    uploadError?.code === "DOCUMENT_FILE_TYPE_MISMATCH"
  ) {
    return "documents.messages.fileTypeUnsupported";
  }
  if (
    uploadError?.code === "DOCUMENT_FILE_SIZE_INVALID" ||
    uploadError?.code === "DOCUMENT_FILE_SIZE_MISMATCH"
  ) {
    return "documents.messages.fileSizeInvalid";
  }
  if (
    uploadError?.code === "DOCUMENT_DIRECT_UPLOAD_FAILED" ||
    uploadError?.code === "DOCUMENT_DIRECT_UPLOAD_MISSING" ||
    uploadError?.code === "DOCUMENT_DIRECT_UPLOAD_UNAVAILABLE"
  ) {
    return "documents.messages.directUploadFailed";
  }
  return null;
}

export async function apiGetHRDocuments(
  employeeId?: number,
): Promise<HRDocument[]> {
  const qs = employeeId ? `?employee_id=${employeeId}` : "";
  return apiFetch(`/api/documents${qs}`);
}

export async function apiGetMyDocuments(): Promise<HRDocument[]> {
  return apiFetch("/api/documents/my");
}

export async function apiGetHRDocument(id: number): Promise<HRDocument> {
  return apiFetch(`/api/documents/${id}`);
}

export interface DocumentVersion {
  id: number;
  documentId: number;
  versionNumber: number;
  fileKey: string;
  fileUrl: string | null;
  mimeType: string;
  fileSize: number;
  uploadedBy: number;
  uploadedByName: string | null;
  createdAt: string;
}

export interface DocumentAuditLog {
  id: number;
  documentId: number;
  actionType: string;
  userId: number;
  userName: string | null;
  userEmail: string | null;
  role: string | null;
  metadata: any;
  createdAt: string;
}

export async function apiGetDocumentAudit(id: number): Promise<{
  versions: DocumentVersion[];
  audit: DocumentAuditLog[];
}> {
  const data = await apiFetch<any>(`/api/documents/${id}/audit`);
  return {
    versions: data.versions.map((v: any) => ({
      id: v.id,
      documentId: v.document_id,
      versionNumber: v.version_number,
      fileKey: v.file_key,
      fileUrl: v.file_url,
      mimeType: v.mime_type,
      fileSize: v.file_size,
      uploadedBy: v.uploaded_by,
      uploadedByName: v.uploaded_by_name,
      createdAt: v.created_at,
    })),
    audit: data.audit.map((a: any) => ({
      id: a.id,
      documentId: a.document_id,
      actionType: a.action_type,
      userId: a.user_id,
      userName: a.user_name,
      userEmail: a.user_email,
      role: a.role,
      metadata: a.metadata,
      createdAt: a.created_at,
    })),
  };
}

export async function apiCreateHRDocument(data: {
  employeeId?: number;
  documentName: string;
  documentType?: DocumentType;
  documentExpiry?: string;
  templateId?: number;
}): Promise<HRDocument> {
  return apiFetch("/api/documents", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUploadHRDocumentFile(
  id: number,
  file: File,
): Promise<HRDocument> {
  const validationCode = validateDocumentUploadFile(file);
  if (validationCode) {
    const messages: Record<DocumentUploadValidationCode, string> = {
      DOCUMENT_FILE_TOO_LARGE: "Document files must be 10 MB or smaller.",
      DOCUMENT_FILE_TYPE_UNSUPPORTED: "This file type is not supported for document uploads.",
      DOCUMENT_FILE_SIZE_INVALID: "The selected document is empty or has an invalid size.",
    };
    throw new ApiError(400, messages[validationCode], validationCode);
  }

  const authorization = await apiFetch<
    | { uploadMode: "multipart"; maxBytes: number }
    | {
        uploadMode: "direct";
        key: string;
        uploadUrl: string;
        headers: Record<string, string>;
        expiresInSeconds: number;
        maxBytes: number;
      }
  >(`/api/documents/${id}/upload-authorization`, {
    method: "POST",
    body: JSON.stringify({
      fileName: file.name,
      fileSize: file.size,
    }),
  });

  if (authorization.uploadMode === "direct") {
    let uploadResponse: Response;
    try {
      uploadResponse = await fetch(authorization.uploadUrl, {
        method: "PUT",
        headers: authorization.headers,
        body: file,
      });
    } catch {
      throw new ApiError(
        502,
        "The storage upload could not be completed. Please try again or contact HR.",
        "DOCUMENT_DIRECT_UPLOAD_FAILED",
      );
    }

    if (!uploadResponse.ok) {
      throw new ApiError(
        502,
        "The storage upload could not be completed. Please try again or contact HR.",
        "DOCUMENT_DIRECT_UPLOAD_FAILED",
      );
    }

    return apiFetch<HRDocument>(`/api/documents/${id}/upload-finalize`, {
      method: "POST",
      body: JSON.stringify({
        key: authorization.key,
        fileName: file.name,
        fileSize: file.size,
      }),
    });
  }

  const formData = new FormData();
  formData.append("file", file);
  return apiMultipartFetch<HRDocument>(`/api/documents/${id}/upload`, formData);
}

export async function apiSignHRDocument(
  id: number,
  signaturePng: string,
): Promise<HRDocument> {
  return apiFetch(`/api/documents/${id}/sign`, {
    method: "POST",
    body: JSON.stringify({ signaturePng }),
  });
}

export async function apiUpdateHRDocumentStatus(
  id: number,
  status: DocumentStatus,
): Promise<HRDocument> {
  return apiFetch(`/api/documents/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function apiDeleteHRDocument(id: number): Promise<void> {
  return apiFetch(`/api/documents/${id}`, { method: "DELETE" });
}

export async function apiGetHRDocumentFileBlob(id: number): Promise<Blob> {
  const response = await apiFetchBlob(`/api/documents/${id}/file`);
  return response.blob;
}

export async function apiDownloadHRDocumentFile(
  id: number,
  fallbackFilename: string,
): Promise<void> {
  return apiDownloadBlob(`/api/documents/${id}/file`, fallbackFilename);
}

export async function apiRejectHRDocument(
  id: number,
  reason: string,
): Promise<HRDocument> {
  return apiFetch(`/api/documents/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function apiUpdateHRDocumentContent(
  id: number,
  content: string,
): Promise<HRDocument> {
  return apiFetch(`/api/documents/${id}/content`, {
    method: "PATCH",
    body: JSON.stringify({ content }),
  });
}

// ---- Document Templates ----

export type DocumentTemplateType =
  | "contract"
  | "annex"
  | "bank"
  | "court"
  | "sick_leave"
  | "custom";

export interface DocumentTemplate {
  id: number;
  name: string;
  templateType: DocumentTemplateType;
  contentText: string | null;
  fileKey: string | null;
  isActive?: boolean;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentTemplateBundle {
  id: number;
  name: string;
  isDefault: boolean;
  templateIds: number[];
  templates: DocumentTemplate[];
  createdAt?: string;
  updatedAt?: string;
}

export async function apiGetDocumentTemplates(): Promise<DocumentTemplate[]> {
  return apiFetch("/api/document-templates");
}

export async function apiGetDefaultDocumentTemplateBundle(): Promise<DocumentTemplateBundle | null> {
  return apiFetch("/api/document-templates/default-bundle");
}

export async function apiUpdateDefaultDocumentTemplateBundle(data: {
  name?: string;
  templateIds: number[];
}): Promise<DocumentTemplateBundle | null> {
  return apiFetch("/api/document-templates/default-bundle", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiCreateDocumentTemplate(data: {
  name: string;
  templateType: DocumentTemplateType;
  contentText?: string;
}): Promise<DocumentTemplate> {
  return apiFetch("/api/document-templates", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiCreateDocumentTemplateFile(
  name: string,
  templateType: DocumentTemplateType,
  file: File,
): Promise<DocumentTemplate> {
  const formData = new FormData();
  formData.append("name", name);
  formData.append("templateType", templateType);
  formData.append("file", file);
  return apiMultipartFetch<DocumentTemplate>("/api/document-templates/upload", formData);
}

export async function apiUpdateDocumentTemplate(
  id: number,
  data: { name: string; templateType: DocumentTemplateType; contentText?: string },
): Promise<DocumentTemplate> {
  return apiFetch(`/api/document-templates/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteDocumentTemplate(id: number): Promise<void> {
  return apiFetch(`/api/document-templates/${id}`, { method: "DELETE" });
}

export interface DocumentTemplateVersion {
  id: number;
  version_number: number;
  name: string;
  template_type: string | null;
  content_text: string | null;
  change_note: string | null;
  changed_at: string;
  changed_by_name: string | null;
}

export async function apiGetDocumentTemplateVersions(templateId: number): Promise<DocumentTemplateVersion[]> {
  return apiFetch(`/api/document-templates/${templateId}/versions`);
}

export async function apiPreviewDocumentTemplate(
  templateId: number,
  employeeId: number,
): Promise<{ content: string; employeeName: string; templateName: string }> {
  return apiFetch(`/api/document-templates/${templateId}/preview`, {
    method: "POST",
    body: JSON.stringify({ employeeId }),
  });
}

export async function apiGenerateDocumentFromTemplate(
  templateId: number,
  data: { employeeId: number; content: string; documentName?: string },
): Promise<{ documentId: number; message: string }> {
  return apiFetch(`/api/document-templates/${templateId}/generate`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getDocumentTemplateFileUrl(id: number): string {
  const userId = getCurrentUserId();
  const params = new URLSearchParams();
  if (userId) params.set("uid", userId);
  params.set("x-tenant", getTenantSlug());
  return `${API_BASE}/api/document-templates/${id}/file?${params.toString()}`;
}

export async function apiGenerateDocumentsFromTemplates(data: {
  employeeId: number;
  templateIds: number[];
}): Promise<{ created: number; documentIds: number[] }> {
  return apiFetch("/api/documents/generate-from-templates", {
    method: "POST",
    body: JSON.stringify(data),
  });
}


// ── Document Chain Templates ────────────────────────────────────────────────

export interface DocumentChainStep {
  order: number;
  type: "notification" | "approval" | "confirmation";
  assignTo: "employee" | "hr" | "line_manager" | "admin";
  label?: string;
}

export interface DocumentChainTemplate {
  id: number;
  name: string;
  steps: DocumentChainStep[];
  created_at: string;
}

export async function apiGetDocumentChainTemplates(): Promise<DocumentChainTemplate[]> {
  return apiFetch("/api/documents/chain-templates");
}

export async function apiSaveDocumentChainTemplate(
  name: string,
  steps: DocumentChainStep[],
): Promise<DocumentChainTemplate> {
  return apiFetch("/api/documents/chain-templates", {
    method: "POST",
    body: JSON.stringify({ name, steps }),
  });
}

export async function apiDeleteDocumentChainTemplate(id: number): Promise<void> {
  return apiFetch(`/api/documents/chain-templates/${id}`, { method: "DELETE" });
}

export async function apiStartDocumentChain(
  documentId: number,
  steps: DocumentChainStep[],
): Promise<{ execId: number }> {
  return apiFetch(`/api/documents/${documentId}/start-chain`, {
    method: "POST",
    body: JSON.stringify({ steps }),
  });
}

export async function apiGetMySignature(): Promise<{
  signaturePng: string | null;
}> {
  return apiFetch("/api/signatures/my");
}

export async function apiSaveMySignature(signaturePng: string): Promise<void> {
  return apiFetch("/api/signatures/my", {
    method: "PUT",
    body: JSON.stringify({ signaturePng }),
  });
}

// ---- Time Off ----

export interface TimeOffRequest {
  id: number;
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  status: string;
  notes: string | null;
  createdAt: string;
}

export interface TimeOffBalance {
  id: number;
  employeeId: string;
  leaveType: string;
  leaveTypeLabel?: string;
  totalDays: number;
  usedDays: number;
  availableNow?: number;
  year: number;
  remainingDays: number;
}

export async function apiGetTimeOff(empId: string): Promise<TimeOffRequest[]> {
  return apiFetch(`/api/employees/${empId}/time-off`);
}

export async function apiCreateTimeOff(
  empId: string,
  data: Omit<TimeOffRequest, "id" | "employeeId" | "createdAt">,
): Promise<TimeOffRequest> {
  return apiFetch(`/api/employees/${empId}/time-off`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiGetTimeOffBalance(
  empId: string,
): Promise<TimeOffBalance[]> {
  return apiFetch(`/api/employees/${empId}/time-off-balance`);
}

// ---- Notes ----

export interface EmployeeNote {
  id: number;
  employeeId: string;
  authorId: number | null;
  authorName: string | null;
  authorInitials: string | null;
  authorAvatarColor: string | null;
  content: string;
  createdAt: string;
}

export async function apiGetNotes(empId: string): Promise<EmployeeNote[]> {
  return apiFetch(`/api/employees/${empId}/notes`);
}

export async function apiCreateNote(
  empId: string,
  data: { authorId: number; content: string },
): Promise<EmployeeNote> {
  return apiFetch(`/api/employees/${empId}/notes`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---- Audit Log ----

export interface AuditLogEntry {
  id: number;
  employeeId: string;
  changedById: number | null;
  changedByName: string | null;
  changedByInitials: string | null;
  changedByAvatarColor: string | null;
  section: string;
  action: "update" | "create" | "delete";
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  changedAt: string;
}

export async function apiGetEmployeeAuditLog(empId: string): Promise<AuditLogEntry[]> {
  return apiFetch(`/api/employees/${empId}/audit-log`);
}

// ---- Performance ----

export interface PerformanceKpi {
  id: number;
  employeeId: number;
  title: string;
  description: string | null;
  targetValue: number;
  currentValue: number;
  unit: string;
  weight: number;
  period: string;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PerformanceGoal {
  id: number;
  employeeId: number;
  title: string;
  description: string | null;
  dueDate: string | null;
  status: "Not Started" | "In Progress" | "Completed" | "Overdue";
  progress: number;
  category: string | null;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PerformanceReview {
  id: number;
  employeeId: number;
  reviewerId: number | null;
  reviewerName: string | null;
  reviewPeriod: string;
  overallRating: number;
  ratingLabel: string;
  strengths: string | null;
  improvements: string | null;
  comments: string | null;
  reviewDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface PerformanceData {
  employeeId: number;
  kpis: PerformanceKpi[];
  goals: PerformanceGoal[];
  reviews: PerformanceReview[];
}

export async function apiGetMyPerformance(): Promise<PerformanceData> {
  return apiFetch("/api/performance/my");
}

export async function apiGetEmployeePerformance(
  employeeId: number,
): Promise<PerformanceData> {
  return apiFetch(`/api/performance/${employeeId}`);
}

export async function apiCreateKpi(
  employeeId: number,
  data: {
    title: string;
    description?: string;
    targetValue?: number;
    currentValue?: number;
    unit?: string;
    weight?: number;
    period?: string;
  },
): Promise<PerformanceKpi> {
  return apiFetch(`/api/performance/${employeeId}/kpis`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiCreateGoal(
  employeeId: number,
  data: {
    title: string;
    description?: string;
    dueDate?: string;
    status?: string;
    progress?: number;
    category?: string;
  },
): Promise<PerformanceGoal> {
  return apiFetch(`/api/performance/${employeeId}/goals`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiCreateReview(
  employeeId: number,
  data: {
    reviewPeriod: string;
    overallRating: number;
    ratingLabel: string;
    strengths?: string;
    improvements?: string;
    comments?: string;
    reviewDate?: string;
  },
): Promise<PerformanceReview> {
  return apiFetch(`/api/performance/${employeeId}/reviews`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateKpi(
  employeeId: number,
  kpiId: number,
  data: {
    title?: string;
    description?: string | null;
    targetValue?: number;
    currentValue?: number;
    unit?: string;
    weight?: number;
    period?: string;
  }
): Promise<PerformanceKpi> {
  return apiFetch(`/api/performance/${employeeId}/kpis/${kpiId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteKpi(
  employeeId: number,
  kpiId: number
): Promise<{ success: boolean }> {
  return apiFetch(`/api/performance/${employeeId}/kpis/${kpiId}`, {
    method: "DELETE",
  });
}

export async function apiUpdateGoal(
  employeeId: number,
  goalId: number,
  data: {
    title?: string;
    description?: string | null;
    dueDate?: string | null;
    status?: string;
    progress?: number;
    category?: string | null;
  }
): Promise<PerformanceGoal> {
  return apiFetch(`/api/performance/${employeeId}/goals/${goalId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteGoal(
  employeeId: number,
  goalId: number
): Promise<{ success: boolean }> {
  return apiFetch(`/api/performance/${employeeId}/goals/${goalId}`, {
    method: "DELETE",
  });
}

export async function apiUpdateReview(
  employeeId: number,
  reviewId: number,
  data: {
    reviewPeriod?: string;
    overallRating?: number;
    ratingLabel?: string;
    strengths?: string | null;
    improvements?: string | null;
    comments?: string | null;
    reviewDate?: string;
  }
): Promise<PerformanceReview> {
  return apiFetch(`/api/performance/${employeeId}/reviews/${reviewId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteReview(
  employeeId: number,
  reviewId: number
): Promise<{ success: boolean }> {
  return apiFetch(`/api/performance/${employeeId}/reviews/${reviewId}`, {
    method: "DELETE",
  });
}

export async function apiRequestGoalMeeting(goalId: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/performance/goals/${goalId}/request-meeting`, { method: "POST" });
}

export type CareerMeetingTopic = "kpis" | "goals" | "reviews" | "career_path";
export type CareerMeetingStatus = Record<CareerMeetingTopic, boolean>;

export async function apiGetCareerMeetingStatus(): Promise<CareerMeetingStatus> {
  return apiFetch("/api/performance/career-meeting-status");
}

export async function apiRequestCareerMeeting(topic: CareerMeetingTopic): Promise<{
  success: boolean;
  assignedTo: "line_manager" | "hr";
  targetName: string;
}> {
  return apiFetch("/api/performance/request-career-meeting", {
    method: "POST",
    body: JSON.stringify({ topic }),
  });
}

// ---- Career Paths ----

export interface CareerPath {
  id: number;
  employeeId: number;
  targetPosition: string;
  targetDepartment: string | null;
  targetTimeline: string | null;
  requiredSkills: string | null;
  developmentActions: string | null;
  notes: string | null;
  setByUserId: number | null;
  setByName: string | null;
  outcome: string | null;
  outcomeNotes: string | null;
  outcomeDate: string | null;
  outcomeSetBy: number | null;
  outcomeSetByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function apiGetMyCareerPath(): Promise<CareerPath[]> {
  return apiFetch("/api/performance/my/career-path");
}

export async function apiGetEmployeeCareerPath(employeeId: number): Promise<CareerPath[]> {
  return apiFetch(`/api/performance/${employeeId}/career-path`);
}

export async function apiCreateCareerPath(
  employeeId: number,
  data: { targetPosition: string; targetDepartment?: string; targetTimeline?: string; requiredSkills?: string; developmentActions?: string; notes?: string; outcome?: string; outcomeNotes?: string },
): Promise<CareerPath> {
  return apiFetch(`/api/performance/${employeeId}/career-path`, { method: "POST", body: JSON.stringify(data) });
}

export async function apiUpdateCareerPath(
  employeeId: number,
  id: number,
  data: { targetPosition?: string; targetDepartment?: string; targetTimeline?: string; requiredSkills?: string; developmentActions?: string; notes?: string; outcome?: string; outcomeNotes?: string },
): Promise<CareerPath> {
  return apiFetch(`/api/performance/${employeeId}/career-path/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function apiDeleteCareerPath(employeeId: number, id: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/performance/${employeeId}/career-path/${id}`, { method: "DELETE" });
}

// ---- Payroll ----

export interface PayrollRunSummary {
  id: number;
  month: number;
  year: number;
  status: string;
  notes: string | null;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  entryCount: number;
  totalGross: number;
  totalNet: number;
  totalContributions: number;
  totalTax: number;
}

export interface PayrollEntry {
  id: number;
  payrollRunId: number;
  employeeId: number;
  employeeName: string | null;
  employeeInitials: string | null;
  avatarColor: string | null;
  avatarUrl: string | null;
  jobTitle: string | null;
  department: string | null;
  employeeCode: string | null;
  grossSalary: number;
  pensionContribution: number;
  healthContribution: number;
  unemploymentContribution: number;
  sicknessContribution: number;
  totalContributions: number;
  grossMinusContributions: number;
  personalExemption: number;
  taxBase: number;
  personalIncomeTax: number;
  totalDeductions: number;
  netSalary: number;
  totalAdjustmentsGross: number;
  totalAdjustmentsNet: number;
  finalNetSalary: number;
  inputType: "gross" | "net";
  adjustments?: any[];
  createdAt?: string;
}

export interface PayrollTotals {
  totalGross: number;
  totalNet: number;
  totalContributions: number;
  totalPension: number;
  totalHealth: number;
  totalUnemployment: number;
  totalSickness: number;
  totalTax: number;
  totalDeductions: number;
  employeeCount: number;
}

export interface PayrollRunDetail extends PayrollRunSummary {
  entries: PayrollEntry[];
  totals: PayrollTotals | null;
}

export async function apiGetPayrollRuns(): Promise<PayrollRunSummary[]> {
  return apiFetch("/api/payroll/runs");
}

export async function apiCreatePayrollRun(data: {
  month: number;
  year: number;
  notes?: string;
}): Promise<PayrollRunSummary> {
  return apiFetch("/api/payroll/runs", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiGetPayrollRun(
  runId: number,
): Promise<PayrollRunDetail> {
  return apiFetch(`/api/payroll/runs/${runId}`);
}

export async function apiUpdatePayrollEntry(
  runId: number,
  entryId: number,
  data: { amount: number; inputType: "gross" | "net" },
): Promise<PayrollEntry> {
  return apiFetch(`/api/payroll/runs/${runId}/entries/${entryId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiUpdatePayrollRunStatus(
  runId: number,
  status: string,
  options?: { sendEmailNotification?: boolean },
): Promise<{ success: boolean }> {
  return apiFetch(`/api/payroll/runs/${runId}/status`, {
    method: "PUT",
    body: JSON.stringify({ status, ...options }),
  });
}

export async function apiDeletePayrollRun(
  runId: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/payroll/runs/${runId}`, { method: "DELETE" });
}

import type { SalaryBreakdown } from "@/lib/mk-salary";

export async function apiCalculateSalary(
  amount: number,
  inputType: "gross" | "net",
): Promise<SalaryBreakdown> {
  return apiFetch("/api/payroll/calculate", {
    method: "POST",
    body: JSON.stringify({ amount, inputType }),
  });
}

// ---- Payroll Adjustments ----

export interface PayrollAdjustment {
  id: number;
  runId: number;
  entryId: number;
  type: string;
  name: string;
  amount: number;
  applyOn: string;
  notes: string | null;
  createdAt: string;
}

export async function apiGetAdjustments(
  runId: number,
  entryId: number,
): Promise<PayrollAdjustment[]> {
  return apiFetch(`/api/payroll/runs/${runId}/entries/${entryId}/adjustments`);
}

export async function apiCreateAdjustment(
  runId: number,
  entryId: number,
  data: {
    type: string;
    name: string;
    amount: number;
    applyOn: string;
    notes?: string;
  },
): Promise<PayrollAdjustment> {
  return apiFetch(`/api/payroll/runs/${runId}/entries/${entryId}/adjustments`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateAdjustment(
  runId: number,
  entryId: number,
  adjId: number,
  data: {
    type?: string;
    name?: string;
    amount?: number;
    applyOn?: string;
    notes?: string;
  },
): Promise<PayrollAdjustment> {
  return apiFetch(
    `/api/payroll/runs/${runId}/entries/${entryId}/adjustments/${adjId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

export async function apiDeleteAdjustment(
  runId: number,
  entryId: number,
  adjId: number,
): Promise<{ success: boolean }> {
  return apiFetch(
    `/api/payroll/runs/${runId}/entries/${entryId}/adjustments/${adjId}`,
    { method: "DELETE" },
  );
}

// ---- Bulk Salary Adjustment ----

export interface BulkAdjustPayload {
  adjustmentType: "percentage" | "fixed";
  value: number;
  filterType: "all" | "department" | "selected";
  departments?: string[];
  employeeIds?: number[];
}

export interface BulkAdjustResult {
  updated: number;
  skipped: number;
  details: {
    updated: { id: number; name: string; oldSalary: number; newSalary: number }[];
    skipped: { id: number; name: string; reason: string }[];
  };
}

export async function apiBulkAdjustSalaries(payload: BulkAdjustPayload): Promise<BulkAdjustResult> {
  return apiFetch("/api/payroll/bulk-adjust", { method: "POST", body: JSON.stringify(payload) });
}

export interface BulkAdjustPreview {
  affectedCount: number;
  skippedCount: number;
  totalCurrentGross: number;
  totalNewGross: number;
  delta: number;
  annualDelta: number;
  byDepartment: { department: string; currentGross: number; newGross: number; delta: number; count: number }[];
}

export async function apiBulkAdjustPreview(payload: Omit<BulkAdjustPayload, "employeeIds">): Promise<BulkAdjustPreview> {
  return apiFetch("/api/payroll/bulk-adjust/preview", { method: "POST", body: JSON.stringify(payload) });
}

// ---- Employee Salary (My Salary) ----

export interface MySalaryPayslip extends PayrollEntry {
  month: number;
  year: number;
  runStatus: string;
  adjustments: PayrollAdjustment[];
}

export interface MySalaryData {
  baseSalary: number;
  payslips: MySalaryPayslip[];
}

export async function apiGetMySalary(): Promise<MySalaryData> {
  return apiFetch("/api/payroll/my");
}

// ---- Organization ----

export interface QuickLink {
  id: string;
  label: string;
  /** "internal" = platform route, "url" = external URL, "file" = uploaded file */
  linkType: "internal" | "url" | "file";
  /** Route path (internal), full URL (url), or file URL (file) */
  href: string;
  /** Lucide icon name, e.g. "BookOpen", "Clock", "Shield" */
  icon: string;
  /** Color name: "blue" | "green" | "orange" | "purple" | "red" | "cyan" | "amber" | "indigo" */
  color: string;
}

export interface CompanyDetails {
  id: number;
  companyName: string;
  legalName: string | null;
  registrationNumber: string | null;
  taxNumber: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  postalCode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  foundedDate: string | null;
  industry: string | null;
  description: string | null;
  logoUrl: string | null;
  defaultAnnualLeaveDays: number;
  timezone: string;
  quickLinks: QuickLink[];
}

export interface OrgStats {
  totalEmployees: number;
  byStatus: { status: string; count: number }[];
  byDepartment: { department: string; count: number }[];
  byContractType: { contractType: string; count: number }[];
  byLocation: { location: string; count: number }[];
  recentEmployees: {
    id: number;
    name: string;
    initials: string;
    avatarColor: string;
    avatarUrl: string | null;
    jobTitle: string;
    department: string;
    startDate: string;
    status: string;
  }[];
  demographics: {
    byTenure: { bucket: string; count: number }[];
    byGender: { gender: string; count: number }[];
  };
  absenceRateByDepartment: { department: string; absenceRate: number }[];
  salaryByDepartment: { department: string; currency: string; avgSalary: number }[];
}

export async function apiGetCompanyDetails(): Promise<CompanyDetails> {
  return apiFetch("/api/organization/company");
}

export async function apiGetCompanyLogo(): Promise<string | null> {
  const data = await apiFetch<{ logoUrl: string | null }>("/api/organization/logo");
  return data.logoUrl;
}

export async function apiGetCompanyName(): Promise<string | null> {
  const data = await apiFetch<{ companyName: string | null }>("/api/organization/company-name");
  return data.companyName;
}

export async function apiUploadCompanyLogo(file: File): Promise<string> {
  const token = getStoredToken();
  const tenantSlug = getTenantSlug();
  const formData = new FormData();
  formData.append("logo", file);
  const res = await fetch(`${API_BASE}/api/organization/logo?_t=${encodeURIComponent(tenantSlug)}`, {
    method: "POST",
    headers: {
      "x-tenant": tenantSlug,
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    },
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || `Upload failed (${res.status})`);
  }
  const data = await res.json();
  return data.logoUrl;
}

export async function apiUploadQuickLinkFile(file: File): Promise<string> {
  const token = getStoredToken();
  const tenantSlug = getTenantSlug();
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_BASE}/api/organization/quick-links/upload?_t=${encodeURIComponent(tenantSlug)}`, {
    method: "POST",
    headers: {
      "x-tenant": tenantSlug,
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    },
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || `Upload failed (${res.status})`);
  }
  const data = await res.json();
  return data.url as string;
}

export async function apiUpdateCompanyDetails(
  data: Partial<Omit<CompanyDetails, "id">>,
): Promise<CompanyDetails> {
  return apiFetch("/api/organization/company", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiGetOrgStats(): Promise<OrgStats> {
  return apiFetch("/api/organization/stats");
}

export interface CreateEmployeePayload {
  name: string;
  email: string;
  jobTitle: string;
  department: string;
  phone?: string;
  team?: string;
  location?: string;
  company?: string;
  status?: string;
  startDate?: string;
  contractType?: string;
  workSchedule?: string;
  birthday?: string;
  nationality?: string;
  address?: string;
  reportsToId?: number | null;
  isLineManager?: boolean;
  employeeId?: string;
  mainRole?: string;
  subRole?: string | null;
  employmentDetails?: Partial<EmploymentDetails>;
  positionId?: number | null;
}

export async function apiCreateEmployee(
  data: CreateEmployeePayload,
): Promise<any> {
  return apiFetch("/api/organization/employees", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---- Bulk CSV import ----

export interface BulkImportResult {
  imported: number;
  skipped: number;
  details: {
    imported: any[];
    skipped: { row: number; reason: string }[];
  };
}

export async function apiBulkImportEmployees(rows: Record<string, any>[]): Promise<BulkImportResult> {
  return apiFetch("/api/organization/employees/bulk", {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

export async function apiBulkImportDepartments(rows: Record<string, any>[]): Promise<BulkImportResult> {
  return apiFetch("/api/organization/departments/bulk", {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

export async function apiBulkImportTeams(rows: Record<string, any>[]): Promise<BulkImportResult> {
  return apiFetch("/api/teams/bulk", {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

export async function apiBulkImportPositions(rows: Record<string, any>[]): Promise<BulkImportResult> {
  return apiFetch("/api/certifications/positions/bulk", {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

export async function apiChangeEmployeeStatus(
  empId: number,
  status: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/organization/employees/${empId}/status`, {
    method: "PUT",
    body: JSON.stringify({ status }),
  });
}

export async function apiDeactivateEmployee(
  empId: number,
): Promise<{ success: boolean; name: string }> {
  return apiFetch(`/api/organization/employees/${empId}`, { method: "DELETE" });
}

// ---- Employee User Role (admin only) ----

export interface EmployeeUserRole {
  userId: string;
  mainRole: "admin" | "hr" | "user";
  subRole: "employee" | "line_manager" | "c_level" | null;
}

export async function apiGetEmployeeUserRole(email: string): Promise<EmployeeUserRole | null> {
  return apiFetch(`/api/auth/employee-role?email=${encodeURIComponent(email)}`);
}

export async function apiUpdateEmployeeUserRole(
  email: string,
  mainRole: string,
  subRole: string | null,
): Promise<EmployeeUserRole> {
  return apiFetch("/api/auth/employee-role", {
    method: "PUT",
    body: JSON.stringify({ email, mainRole, subRole }),
  });
}

// ---- Departments ----

export interface Department {
  id: number;
  name: string;
  description: string | null;
  icon: string | null;
  headEmployeeId: number | null;
  headName: string | null;
  headInitials: string | null;
  headAvatarColor: string | null;
  headJobTitle: string | null;
  employeeCount: number;
  createdAt: string;
  updatedAt: string;
}

export async function apiGetDepartments(): Promise<Department[]> {
  return apiFetch("/api/organization/departments");
}

export async function apiCreateDepartment(data: {
  name: string;
  description?: string;
  headEmployeeId?: number;
  icon?: string;
}): Promise<Department> {
  return apiFetch("/api/organization/departments", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateDepartment(
  id: number,
  data: { name?: string; description?: string; headEmployeeId?: number | null; icon?: string | null },
): Promise<Department> {
  return apiFetch(`/api/organization/departments/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteDepartment(
  id: number,
  reassignTo?: string,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/organization/departments/${id}`, {
    method: "DELETE",
    body: JSON.stringify({ reassignTo }),
  });
}

// ---- Medical Checkups ----

export type MedicalCheckStatus = "valid" | "due_soon" | "expired" | "failed" | "no_exam";
export type MedicalCheckAssignmentStatus = "pending" | "accepted" | "attended" | "absent" | "rejected" | "completed" | "cancelled" | "passed" | "failed";
export type MedicalCheckAttendanceStatus = "not_recorded" | "attended" | "absent";
export type MedicalCheckFrequencySource = "position" | "team" | "department" | "company";

export interface MedicalCheckEmployeeRecord {
  employeeId: number;
  employeeName: string;
  employeeEmail: string | null;
  jobTitle: string | null;
  department: string | null;
  departmentId: number | null;
  team: string | null;
  positionId: number | null;
  positionTitle: string | null;
  frequencyMonths: number;
  frequencySource: MedicalCheckFrequencySource;
  recordId: number | null;
  lastCheckDate: string | null;
  nextDueDate: string | null;
  status: MedicalCheckStatus;
  latestExamDate: string | null;
  latestResult: "passed" | "failed" | null;
  appointmentId: number | null;
  appointmentStatus: "pending" | "accepted" | null;
  appointmentDate: string | null;
  appointmentTime: string | null;
  appointmentInstitution: string | null;
  confirmedByEmployee: boolean;
  confirmedAt: string | null;
  supportingDocumentId: number | null;
  daysUntilDue: number | null;
}

export interface MedicalCheckRule {
  id: number;
  scope: "department" | "team" | "position";
  departmentId: number | null;
  departmentName: string | null;
  teamName: string | null;
  positionId: number | null;
  positionTitle: string | null;
  frequencyMonths: number;
  createdAt: string;
  updatedAt: string;
}

export interface MedicalCheckAssignment {
  id: number;
  groupId: number | null;
  employeeId: number;
  employeeName: string;
  employeeEmail: string | null;
  department: string | null;
  scheduledDate: string;
  scheduledTime: string | null;
  institution: string;
  notes: string | null;
  status: MedicalCheckAssignmentStatus;
  attendanceStatus: MedicalCheckAttendanceStatus;
  attendanceRecordedAt: string | null;
  rejectionReason: string | null;
  employeeRespondedAt: string | null;
  completedAt: string | null;
  supportingDocumentId: number | null;
  supportingDocumentName: string | null;
  canComplete: boolean;
  archived: boolean;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MedicalCheckCompanySettings {
  intervalMonths: number;
  graceMonths: number;
  leadMonths: number;
  timezone?: string;
}

export interface MedicalCheckDashboard {
  employees: MedicalCheckEmployeeRecord[];
  rules: MedicalCheckRule[];
  assignments: MedicalCheckAssignment[];
  departments: { id: number; name: string }[];
  teams: string[];
  settings: MedicalCheckCompanySettings;
  stats: Record<MedicalCheckStatus | "total", number>;
}

export async function apiGetMedicalChecks(): Promise<MedicalCheckDashboard> {
  return apiFetch("/api/medical-checks");
}

export async function apiGetMedicalCheckAssignment(
  assignmentId: number,
  options?: { legacyEmployeeId?: boolean },
): Promise<MedicalCheckAssignment> {
  const suffix = options?.legacyEmployeeId ? "?legacyEmployeeId=true" : "";
  return apiFetch(`/api/medical-checks/assignments/${assignmentId}${suffix}`);
}

export async function apiUpdateMedicalCheckCompanySettings(
  data: Partial<MedicalCheckCompanySettings>,
): Promise<MedicalCheckCompanySettings> {
  return apiFetch("/api/medical-checks/company-settings", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiCreateMedicalCheckAssignments(data: {
  employeeIds: number[];
  scheduledDate: string;
  scheduledTime?: string | null;
  institution: string;
  notes?: string | null;
}): Promise<{ success: boolean; created: number; employeesNotified: number; assignments: Array<{ assignmentId: number; employeeId: number; employeeName: string }> }> {
  return apiFetch("/api/medical-checks/assignments", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiRespondToMedicalCheckAssignment(
  assignmentId: number,
  data: { action: "accept" | "reject"; rejectionReason?: string },
): Promise<{ success: boolean; status: string }> {
  return apiFetch(`/api/medical-checks/assignments/${assignmentId}/respond`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiCompleteMedicalCheckAssignment(
  assignmentId: number,
  data: { result: "passed" | "failed"; supportingDocumentId?: number | null },
): Promise<{ success: boolean }> {
  return apiFetch(`/api/medical-checks/assignments/${assignmentId}/complete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiCancelMedicalCheckAssignment(
  assignmentId: number,
  scope: "individual" | "group" = "individual",
): Promise<{ success: boolean }> {
  return apiFetch(`/api/medical-checks/assignments/${assignmentId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ scope }),
  });
}

export async function apiUpdateMedicalCheckAssignment(
  assignmentId: number,
  data: {
    scope: "individual" | "group";
    scheduledDate: string;
    scheduledTime: string;
    institution: string;
    notes?: string | null;
  },
): Promise<{ success: boolean; updated: number }> {
  return apiFetch(`/api/medical-checks/assignments/${assignmentId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiArchiveMedicalCheckAssignment(
  assignmentId: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/medical-checks/assignments/${assignmentId}/archive`, { method: "POST" });
}

export async function apiAddToMedicalCheckGroup(
  groupId: number,
  employeeIds: number[],
): Promise<{ success: boolean; added: number }> {
  return apiFetch(`/api/medical-checks/assignment-groups/${groupId}/members`, {
    method: "POST",
    body: JSON.stringify({ employeeIds }),
  });
}

export async function apiRecordMedicalCheckGroupAttendance(
  groupId: number,
  attendeeAssignmentIds: number[],
): Promise<{ success: boolean; attended: number; absent: number }> {
  return apiFetch(`/api/medical-checks/assignment-groups/${groupId}/attendance`, {
    method: "POST",
    body: JSON.stringify({ attendeeAssignmentIds }),
  });
}

export async function apiRemoveFromMedicalCheckGroup(
  groupId: number,
  assignmentId: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/medical-checks/assignment-groups/${groupId}/members/${assignmentId}`, {
    method: "DELETE",
  });
}

type MedicalCheckRuleScope =
  | { departmentId: number }
  | { teamName: string }
  | { positionId: number };

function medicalCheckRulePath(scope: MedicalCheckRuleScope): string {
  if ("departmentId" in scope) return `/api/medical-check-rules/${scope.departmentId}`;
  if ("positionId" in scope) return `/api/medical-check-rules/position:${scope.positionId}`;
  return `/api/medical-check-rules/team:${encodeURIComponent(scope.teamName)}`;
}

export async function apiUpdateMedicalCheckRule(
  scope: MedicalCheckRuleScope,
  frequencyMonths: number,
): Promise<MedicalCheckRule> {
  return apiFetch(medicalCheckRulePath(scope), {
    method: "PUT",
    body: JSON.stringify({ frequencyMonths }),
  });
}

export async function apiDeleteMedicalCheckRule(
  scope: MedicalCheckRuleScope,
): Promise<{ success: boolean }> {
  return apiFetch(medicalCheckRulePath(scope), { method: "DELETE" });
}

// ---- Leave Type Configs ----

export interface LeaveTypeConfig {
  id: number;
  name: string;
  label: string;
  color: string;
  isPaid: boolean;
  countsAgainstBalance: boolean;
  requiresDocument: "never" | "always" | "over_3_working_days";
  maxDaysPerYear: number | null;
  isActive: boolean;
  sortOrder: number;
}

export async function apiGetLeaveTypes(): Promise<LeaveTypeConfig[]> {
  return apiFetch("/api/leave/types");
}

export async function apiCreateLeaveType(
  data: Omit<LeaveTypeConfig, "id" | "isActive" | "name">,
): Promise<LeaveTypeConfig> {
  return apiFetch("/api/leave/types", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateLeaveType(
  id: number,
  data: Partial<Omit<LeaveTypeConfig, "id">>,
): Promise<LeaveTypeConfig> {
  return apiFetch(`/api/leave/types/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteLeaveType(
  id: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/leave/types/${id}`, { method: "DELETE" });
}

// ---- Leave Policies ----

export interface LeavePolicy {
  id: number;
  country: string;
  leaveType: string;
  leaveTypeLabel: string;
  leaveTypeColor: string;
  defaultDaysPerYear: number;
  carryOverMaxDays: number;
  carryOverExpiryRule: "june30" | "december31" | "none";
  statutoryMinimumDays: number;
  sickDocRequiredAfterDays: number;
  notes: string | null;
}

export async function apiGetLeavePolicies(
  country: string,
): Promise<LeavePolicy[]> {
  return apiFetch(`/api/leave/policies?country=${encodeURIComponent(country)}`);
}

export async function apiUpdateLeavePolicy(
  country: string,
  leaveType: string,
  data: Partial<
    Pick<
      LeavePolicy,
      | "defaultDaysPerYear"
      | "carryOverMaxDays"
      | "carryOverExpiryRule"
      | "statutoryMinimumDays"
      | "sickDocRequiredAfterDays"
      | "notes"
    >
  >,
): Promise<LeavePolicy> {
  return apiFetch(
    `/api/leave/policies/${encodeURIComponent(country)}/${encodeURIComponent(leaveType)}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
}

// ---- Company Leave Policy ----

export interface LeaveCompanyPolicy {
  annualLeaveDays: number;
  annualLeaveCarryOverMax: number;
  annualLeaveExpiryMonth: number | null;   // 1-12, null = no expiry
  annualLeaveExpiryDay: number | null;     // 1-31, null = no expiry
  sickLeaveDays: number;
}

export async function apiGetLeaveCompanyPolicy(): Promise<LeaveCompanyPolicy> {
  return apiFetch("/api/leave/company-policy");
}

export async function apiUpdateLeaveCompanyPolicy(
  data: LeaveCompanyPolicy,
): Promise<{ ok: boolean }> {
  return apiFetch("/api/leave/company-policy", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export interface LeaveDocumentSettings {
  leaveType: "sick";
  enabled: boolean;
  daysWithoutDocument: number;
  specialistRequiredAfterDays: number;
  standardDocumentLabel: string;
  standardDocumentDescription: string;
  specialistDocumentLabel: string;
  specialistDocumentDescription: string;
  mandatoryUploadsEnabled: boolean;
}

export async function apiGetLeaveDocumentSettings(): Promise<LeaveDocumentSettings> {
  return apiFetch("/api/leave/document-settings");
}

export async function apiUpdateLeaveDocumentSettings(
  data: LeaveDocumentSettings,
): Promise<LeaveDocumentSettings> {
  return apiFetch("/api/leave/document-settings", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ---- Public Holidays ----

export interface PublicHoliday {
  id: number;
  country: string;
  year: number;
  date: string;
  name: string;
  next_monday_off?: boolean;
}

export async function apiGetPublicHolidays(
  country: string,
  year: number,
): Promise<PublicHoliday[]> {
  return apiFetch(
    `/api/leave/holidays?country=${encodeURIComponent(country)}&year=${year}`,
  );
}

export async function apiGetAllPublicHolidays(country?: string): Promise<PublicHoliday[]> {
  const qs = country ? `?country=${encodeURIComponent(country)}` : "";
  return apiFetch(`/api/leave/holidays/all${qs}`);
}

export async function apiAddPublicHoliday(data: {
  country: string;
  date: string;
  name: string;
}): Promise<PublicHoliday> {
  return apiFetch("/api/leave/holidays", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiBulkImportPublicHolidays(
  country: string,
  holidays: { date: string; name: string }[],
): Promise<{ inserted: number; holidays: PublicHoliday[] }> {
  return apiFetch("/api/leave/holidays/bulk", {
    method: "POST",
    body: JSON.stringify({ country, holidays }),
  });
}

export async function apiUpdatePublicHoliday(
  id: number,
  data: { next_monday_off?: boolean; name?: string },
): Promise<PublicHoliday> {
  return apiFetch(`/api/leave/holidays/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function apiDeletePublicHoliday(id: number): Promise<void> {
  return apiFetch(`/api/leave/holidays/${id}`, { method: "DELETE" });
}

export interface LeaveDocumentRequirement {
  country: string;
  countryCode: string;
  workingDays: number;
  calendarDays: number;
  requiresDocument: boolean;
  minDaysRequired: number | null;
  ruleSource: "country_policy" | "leave_type" | "tenant_sick_leave_settings";
  documentType: "standard" | "specialist" | null;
  documentLabel: string | null;
  documentDescription: string | null;
  mandatoryUpload: boolean;
}

export async function apiGetLeaveDocumentRequirement(data: {
  leaveType: string;
  startDate: string;
  endDate: string;
  employeeId?: number;
}): Promise<LeaveDocumentRequirement> {
  return apiFetch("/api/leave/document-requirement", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---- Leave Requests ----

export interface LeaveRequest {
  id: number;
  employeeId: number;
  employeeName: string;
  employeeInitials: string;
  employeeAvatarColor: string;
  leaveType: string;
  leaveTypeLabel: string | null;
  leaveTypeColor: string | null;
  startDate: string;
  endDate: string;
  workingDays: number;
  status:
    | "Pending"
    | "Approved"
    | "Rejected"
    | "Cancelled"
    | "In Review"
    | "Pending Employee Document"
    | "Document Uploaded"
    | "HR Review Required";
  notes: string | null;
  rejectionReason: string | null;
  documentUrl: string | null;
  documentOriginalName: string | null;
  documentRequired: boolean;
  requiredDocumentType: "standard" | "specialist" | null;
  requiredDocumentLabel: string | null;
  requiredDocumentDescription: string | null;
  createdOnBehalf: boolean;
  createdByName: string | null;
  replacementEmployeeId: number | null;
  replacementEmployeeName: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeaveRequestsParams {
  year?: number;
  type?: string;
  status?: string;
  employeeId?: number;
  scope?: "mine";
}

export async function apiGetLeaveRequests(
  params?: LeaveRequestsParams,
): Promise<LeaveRequest[]> {
  const query = new URLSearchParams();
  if (params?.year) query.set("year", String(params.year));
  if (params?.type) query.set("type", params.type);
  if (params?.status) query.set("status", params.status);
  if (params?.employeeId) query.set("employeeId", String(params.employeeId));
  if (params?.scope) query.set("scope", params.scope);
  const qs = query.toString();
  return apiFetch(`/api/leave/requests${qs ? `?${qs}` : ""}`);
}

export async function apiGetMyLeaveRequests(
  params?: Omit<LeaveRequestsParams, "employeeId" | "scope">,
): Promise<LeaveRequest[]> {
  return apiGetLeaveRequests({ ...params, scope: "mine" });
}

export async function apiSubmitLeaveRequest(
  formData: FormData,
): Promise<LeaveRequest> {
  return apiMultipartFetch<LeaveRequest>("/api/leave/requests", formData);
}

export async function apiCreateLeaveForEmployee(
  formData: FormData,
): Promise<LeaveRequest> {
  return apiMultipartFetch<LeaveRequest>("/api/leave/requests/for-employee", formData);
}

export async function apiUploadLeaveDocument(
  id: number,
  formData: FormData,
): Promise<LeaveRequest> {
  return apiMultipartFetch<LeaveRequest>(`/api/leave/requests/${id}/document`, formData);
}

export async function apiGetLeaveRequest(id: number): Promise<LeaveRequest> {
  return apiFetch(`/api/leave/requests/${id}`);
}

export async function apiGetMyLeaveRequest(id: number): Promise<LeaveRequest> {
  return apiFetch(`/api/leave/requests/${id}?scope=mine`);
}

export async function apiCancelLeaveRequest(
  id: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/leave/requests/${id}`, { method: "DELETE" });
}

export async function apiApproveLeaveRequest(
  id: number,
): Promise<LeaveRequest> {
  return apiFetch(`/api/leave/requests/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function apiRejectLeaveRequest(
  id: number,
  rejectionReason: string,
): Promise<LeaveRequest> {
  return apiFetch(`/api/leave/requests/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ rejectionReason }),
  });
}

// ---- Leave Balance ----

export interface LeaveBalance {
  leaveType: string;
  leaveTypeLabel: string;
  leaveTypeColor: string;
  countsAgainstBalance: boolean;
  totalDays: number | null;
  usedDays: number;
  carryOverDays: number;
  carryOverExpiry: string | null;
  pendingDays: number;
  availableNow: number | null;
  year: number;
}

export async function apiGetMyLeaveBalance(
  year?: number,
): Promise<LeaveBalance[]> {
  const qs = year ? `?year=${year}` : "";
  return apiFetch(`/api/leave/balance${qs}`);
}

export async function apiGetEmployeeLeaveBalance(
  employeeId: number,
  year?: number,
): Promise<LeaveBalance[]> {
  const qs = year ? `?year=${year}` : "";
  return apiFetch(`/api/leave/balance/${employeeId}${qs}`);
}

export async function apiAdjustEmployeeLeaveBalance(
  employeeId: number,
  leaveType: string,
  year: number,
  totalDays: number,
): Promise<void> {
  return apiFetch(`/api/leave/balance/${employeeId}/adjust`, {
    method: "PUT",
    body: JSON.stringify({ leaveType, year, totalDays }),
  });
}

export async function apiTriggerCarryOver(fromYear: number): Promise<{
  success: boolean;
  processed: number;
  fromYear: number;
  toYear: number;
}> {
  return apiFetch("/api/leave/balance/carry-over", {
    method: "POST",
    body: JSON.stringify({ fromYear }),
  });
}

// ---- Leave Calendar ----

export interface LeaveCalendarRequest {
  id: number;
  employeeId: number;
  employeeName: string;
  employeeInitials: string;
  employeeAvatarColor: string;
  employeeAvatarUrl: string | null;
  leaveType: string;
  leaveTypeLabel: string | null;
  leaveTypeColor: string | null;
  startDate: string;
  endDate: string;
  status: string;
  replacementEmployeeName: string | null;
}

export interface LeaveCalendarData {
  requests: LeaveCalendarRequest[];
  holidays: { date: string; name: string }[];
}

export async function apiGetLeaveCalendar(
  month: number,
  year: number,
  country?: string,
  scope?: "mine",
): Promise<LeaveCalendarData> {
  const query = new URLSearchParams({
    month: String(month),
    year: String(year),
  });
  if (country) query.set("country", country);
  if (scope) query.set("scope", scope);
  return apiFetch(`/api/leave/calendar?${query.toString()}`);
}

export async function apiGetMyLeaveCalendar(
  month: number,
  year: number,
  country?: string,
): Promise<LeaveCalendarData> {
  return apiGetLeaveCalendar(month, year, country, "mine");
}

// ---- Team Leave Overlap ----

export interface TeamLeaveOverlapEntry {
  employeeId: number;
  employeeName: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  status: string;
}

export interface TeamLeaveOverlap {
  teamSize: number;
  onLeave: TeamLeaveOverlapEntry[];
  onLeaveCount: number;
  workingCount: number;
}

export async function apiGetTeamLeaveOverlap(
  employeeId: number,
  startDate: string,
  endDate: string,
): Promise<TeamLeaveOverlap> {
  return apiFetch(
    `/api/leave/team-overlap?employeeId=${employeeId}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
  );
}

export interface LeaveReplacementCandidate {
  id: number;
  name: string;
  jobTitle: string | null;
  department: string | null;
  status: string;
}

export interface LeaveReplacementAssignment {
  id: number;
  requestId: number;
  employeeId: number;
  employeeName: string;
  leaveType: string;
  leaveTypeLabel: string | null;
  leaveTypeColor: string | null;
  startDate: string;
  endDate: string;
  workingDays: number;
  status: LeaveRequest["status"];
  notes: string | null;
}

export async function apiGetLeaveReplacementCandidates(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<LeaveReplacementCandidate[]> {
  const query = new URLSearchParams();
  if (params?.startDate) query.set("startDate", params.startDate);
  if (params?.endDate) query.set("endDate", params.endDate);
  const qs = query.toString();
  return apiFetch(`/api/leave/replacement-candidates${qs ? `?${qs}` : ""}`);
}

export async function apiGetMyLeaveReplacementAssignments(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<LeaveReplacementAssignment[]> {
  const query = new URLSearchParams();
  if (params?.startDate) query.set("startDate", params.startDate);
  if (params?.endDate) query.set("endDate", params.endDate);
  const qs = query.toString();
  return apiFetch(`/api/leave/replacement-assignments${qs ? `?${qs}` : ""}`);
}

// ---- Notifications ----

export interface AppNotification {
  id: number;
  type:
    | "leave_submitted"
    | "leave_approved"
    | "leave_rejected"
    | "leave_replacement"
    | "leave_cancelled"
    | "leave_created_on_behalf"
    | "leave_document_uploaded"
    | "document_uploaded"
    | "document_completed"
    | "document_rejected"
    | "document_request"
    | "event_new"
    | "salary_processed"
    | "gdpr_retention"
    | "recruitment_application_received"
    | "team_join_request"
    | "team_request_approved"
    | "team_request_rejected"
    | string;
  title: string;
  message: string;
  entityType: string;
  entityId: number | null;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationsResponse {
  notifications: AppNotification[];
  unreadCount: number;
  total: number;
}

export async function apiGetNotifications(
  limit = 20,
  offset = 0,
): Promise<NotificationsResponse> {
  return apiFetch(`/api/notifications?limit=${limit}&offset=${offset}`);
}

export async function apiGetNotificationUnreadCount(): Promise<{
  count: number;
}> {
  return apiFetch("/api/notifications/unread-count");
}

export async function apiMarkNotificationRead(
  id: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/notifications/${id}/read`, { method: "PUT" });
}

export async function apiMarkAllNotificationsRead(): Promise<{
  updated: number;
}> {
  return apiFetch("/api/notifications/read-all", { method: "PUT" });
}

export async function apiGetTeamJoinRequestsPendingCount(): Promise<{
  count: number;
}> {
  return apiFetch("/api/teams/join-requests/pending-count");
}

// ============================================================
// Onboarding
// ============================================================

export interface OnboardingTemplate {
  id: number;
  name: string;
  description: string | null;
  targetRole: string | null;
  targetDepartment: string | null;
  createdBy: number;
  createdByEmail?: string;
  isActive: boolean;
  taskCount: number;
  activeSessions: number;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingTemplateTask {
  id: number;
  templateId: number;
  title: string;
  description: string | null;
  category: string;
  dayOffset: number;
  isRequired: boolean;
  assigneeType: string;
  taskType: string;
  documentTemplateIds: number[];
  sortOrder: number;
}

export interface OnboardingTask {
  id: number;
  sessionId: number;
  templateTaskId: number | null;
  title: string;
  description: string | null;
  category: string;
  dueDate: string | null;
  status: "pending" | "in_progress" | "completed" | "skipped";
  assigneeType: string;
  assigneeId: number | null;
  assigneeName: string | null;
  canEdit?: boolean;
  completedAt: string | null;
  notes: string | null;
  documentIds: number[];
  taskType: string;
  sortOrder: number;
}

export interface OnboardingSession {
  id: number;
  employeeId: number;
  employeeName: string;
  employeeRole: string | null;
  employeeDepartment: string | null;
  templateId: number | null;
  templateName: string | null;
  startedBy: number;
  startDate: string;
  expectedEndDate: string | null;
  status: "active" | "completed" | "paused" | "cancelled";
  progress: number;
  buddyId: number | null;
  buddyName: string | null;
  buddyAvatarUrl?: string | null;
  buddyAvatarColor?: string | null;
  documentBundleId: number | null;
  documentTemplateIds: number[];
  notes: string | null;
  tasks: OnboardingTask[];
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingKBArticle {
  id: number;
  title: string;
  content: string;
  category: string | null;
  tags: string[];
  createdBy: number;
  createdByEmail?: string;
  isPublished: boolean;
  fileKeys: string[];
  fileNames: string[];
  hasExtractedText: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingChatMessage {
  id: number;
  sessionId: number;
  userId: number;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

export interface OnboardingStats {
  activeSessions: number;
  completedSessions: number;
  totalSessions: number;
  avgDurationDays: number;
  avgProgress: number;
  templateCount: number;
  kbArticleCount: number;
  completionRate: number;
  recentSessions: {
    id: number;
    status: string;
    progress: number;
    startDate: string;
    employeeName: string;
    templateName: string | null;
    createdAt: string;
  }[];
  progressDistribution: { range: string; count: number }[];
}

// Templates
export async function apiGetOnboardingTemplates(): Promise<
  OnboardingTemplate[]
> {
  return apiFetch("/api/onboarding/templates");
}
export async function apiCreateOnboardingTemplate(data: {
  name: string;
  description?: string;
  targetRole?: string;
  targetDepartment?: string;
}): Promise<OnboardingTemplate> {
  return apiFetch("/api/onboarding/templates", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiUpdateOnboardingTemplate(
  id: number,
  data: Partial<{
    name: string;
    description: string;
    targetRole: string;
    targetDepartment: string;
  }>,
): Promise<OnboardingTemplate> {
  return apiFetch(`/api/onboarding/templates/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteOnboardingTemplate(
  id: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/onboarding/templates/${id}`, { method: "DELETE" });
}

// Template tasks
export async function apiGetTemplateTasks(
  templateId: number,
): Promise<OnboardingTemplateTask[]> {
  return apiFetch(`/api/onboarding/templates/${templateId}/tasks`);
}
export async function apiCreateTemplateTask(
  templateId: number,
  data: {
    title: string;
    description?: string;
    category?: string;
    dayOffset?: number;
    isRequired?: boolean;
    assigneeType?: string;
    taskType?: string;
    documentTemplateIds?: number[];
    sortOrder?: number;
  },
): Promise<OnboardingTemplateTask> {
  return apiFetch(`/api/onboarding/templates/${templateId}/tasks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiUpdateTemplateTask(
  templateId: number,
  taskId: number,
  data: Partial<OnboardingTemplateTask>,
): Promise<OnboardingTemplateTask> {
  return apiFetch(`/api/onboarding/templates/${templateId}/tasks/${taskId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteTemplateTask(
  templateId: number,
  taskId: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/onboarding/templates/${templateId}/tasks/${taskId}`, {
    method: "DELETE",
  });
}

// Sessions
export async function apiGetOnboardingSessions(): Promise<OnboardingSession[]> {
  return apiFetch("/api/onboarding/sessions");
}
export async function apiCreateOnboardingSession(data: {
  employeeId: number;
  templateId?: number;
  startDate?: string;
  buddyId?: number;
  notes?: string;
  documentTemplateIds?: number[];
}): Promise<OnboardingSession> {
  return apiFetch("/api/onboarding/sessions", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiGetOnboardingSession(
  id: number,
): Promise<OnboardingSession> {
  return apiFetch(`/api/onboarding/sessions/${id}`);
}
export async function apiUpdateOnboardingSession(
  id: number,
  data: Partial<{ status: string; buddyId: number; notes: string }>,
): Promise<OnboardingSession> {
  return apiFetch(`/api/onboarding/sessions/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteOnboardingSession(
  id: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/onboarding/sessions/${id}`, { method: "DELETE" });
}

// Session tasks
export async function apiUpdateOnboardingTask(
  sessionId: number,
  taskId: number,
  data: { status?: string; notes?: string; documentIds?: number[] },
): Promise<OnboardingTask & { progress: number; sessionStatus: OnboardingSession["status"] }> {
  return apiFetch(`/api/onboarding/sessions/${sessionId}/tasks/${taskId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiAddOnboardingTask(
  sessionId: number,
  data: {
    title: string;
    description?: string;
    category?: string;
    dueDate?: string;
    assigneeType?: string;
  },
): Promise<OnboardingTask & { expectedEndDate?: string | null }> {
  return apiFetch(`/api/onboarding/sessions/${sessionId}/tasks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Employee portal
export async function apiGetMyOnboarding(): Promise<OnboardingSession | null> {
  return apiFetch("/api/onboarding/my");
}
export async function apiGetMyOnboardingChat(): Promise<
  OnboardingChatMessage[]
> {
  return apiFetch("/api/onboarding/my/chat");
}
export async function apiSendOnboardingChat(
  message: string,
): Promise<{ role: string; content: string }> {
  return apiFetch("/api/onboarding/my/chat", {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}
export async function apiEscalateOnboardingChat(
  target: "manager" | "buddy" | "hr",
  question: string,
): Promise<{ success: boolean; sentTo: string; target: string }> {
  return apiFetch("/api/onboarding/my/chat/escalate", {
    method: "POST",
    body: JSON.stringify({ target, question }),
  });
}

// Knowledge base
export async function apiGetOnboardingKB(): Promise<OnboardingKBArticle[]> {
  return apiFetch("/api/onboarding/kb");
}
export async function apiCreateOnboardingKBArticle(data: {
  title: string;
  content: string;
  category?: string;
  tags?: string[];
  isPublished?: boolean;
}): Promise<OnboardingKBArticle> {
  return apiFetch("/api/onboarding/kb", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiUpdateOnboardingKBArticle(
  id: number,
  data: Partial<{
    title: string;
    content: string;
    category: string;
    tags: string[];
    isPublished: boolean;
  }>,
): Promise<OnboardingKBArticle> {
  return apiFetch(`/api/onboarding/kb/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteOnboardingKBArticle(
  id: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/onboarding/kb/${id}`, { method: "DELETE" });
}
export async function apiUploadKBFiles(
  articleId: number,
  files: File[],
): Promise<OnboardingKBArticle> {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  const userId = getCurrentUserId();
  const headers: Record<string, string> = { "x-tenant": getTenantSlug() };
  if (userId) headers["x-user-id"] = userId;
  const resp = await fetch(`${API_BASE}/api/onboarding/kb/${articleId}/files`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}
export async function apiDeleteKBFile(
  articleId: number,
  fileIndex: number,
): Promise<OnboardingKBArticle> {
  return apiFetch(`/api/onboarding/kb/${articleId}/files/${fileIndex}`, {
    method: "DELETE",
  });
}
export async function apiReextractKBText(
  articleId: number,
): Promise<OnboardingKBArticle> {
  return apiFetch(`/api/onboarding/kb/${articleId}/reextract`, {
    method: "POST",
  });
}

// Onboarding messages
export interface OnboardingMessageThread {
  threadId: string;
  sessionId: number | null;
  lastMessage: string;
  lastAt: string;
  otherUserId: number;
  otherName: string;
  otherAvatarUrl?: string | null;
  employeeName: string | null;
  unreadCount: number;
}
export interface OnboardingMessage {
  id: number;
  threadId: string;
  sessionId: number | null;
  senderId: number;
  senderName: string;
  recipientId: number;
  content: string;
  isRead: boolean;
  createdAt: string;
}
export async function apiGetOnboardingThreads(): Promise<
  OnboardingMessageThread[]
> {
  return apiFetch("/api/onboarding/messages");
}
export async function apiGetOnboardingThread(
  threadId: string,
): Promise<OnboardingMessage[]> {
  return apiFetch(`/api/onboarding/messages/${threadId}`);
}
export async function apiReplyOnboardingThread(
  threadId: string,
  content: string,
): Promise<OnboardingMessage> {
  return apiFetch(`/api/onboarding/messages/${threadId}`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
}
export async function apiComposeOnboardingMessage(
  target: "manager" | "buddy" | "hr",
  content: string,
): Promise<{ success: boolean; threadId: string; sentTo: string }> {
  return apiFetch("/api/onboarding/messages/compose", {
    method: "POST",
    body: JSON.stringify({ target, content }),
  });
}

export async function apiComposeDirectMessage(
  recipientEmployeeId: number,
  content: string,
): Promise<{ success: boolean; threadId: string; sentTo: string }> {
  return apiFetch("/api/onboarding/messages/compose-direct-message", {
    method: "POST",
    body: JSON.stringify({ recipientEmployeeId, content }),
  });
}

// AI
export async function apiGenerateOnboardingTasks(data: {
  role: string;
  department: string;
  templateTasks?: any[];
}): Promise<{ tasks: any[] }> {
  return apiFetch("/api/onboarding/ai/generate-tasks", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Stats
export async function apiGetOnboardingStats(): Promise<OnboardingStats> {
  return apiFetch("/api/onboarding/stats");
}

// ══════════════════════════════════════════
// Surveys
// ══════════════════════════════════════════

export interface SurveyOption {
  id: number;
  optionText: string;
  sortOrder: number;
}

export interface SurveyQuestion {
  id: number;
  questionText: string;
  questionType: "single_choice" | "multiple_choice" | "text" | "rating";
  isRequired: boolean;
  sortOrder: number;
  options: SurveyOption[];
}

export interface Survey {
  id: number;
  title: string;
  description: string | null;
  isAnonymous: boolean;
  status: "draft" | "active" | "closed";
  createdBy: number;
  createdByName: string;
  startDate: string | null;
  endDate: string | null;
  questionCount: number;
  responseCount: number;
  totalEmployees?: number;
  hasResponded?: boolean;
  createdAt: string;
}

export interface SurveyDetail extends Survey {
  questions: SurveyQuestion[];
}

export interface SurveyResultOptionCount {
  optionId: number;
  optionText: string;
  count: number;
}

export interface SurveyQuestionResult {
  questionId: number;
  questionText: string;
  questionType: string;
  options: SurveyOption[];
  optionCounts?: SurveyResultOptionCount[];
  totalResponses?: number;
  textAnswers?: { text: string; respondentName?: string }[];
  avgRating?: number;
  ratingCount?: number;
  ratingDistribution?: { rating: number; count: number }[];
}

export interface SurveyResults {
  surveyId: number;
  isAnonymous: boolean;
  responseCount: number;
  totalEmployees: number;
  respondents: {
    responseId: number;
    employeeName: string;
    department: string;
    submittedAt: string;
    answers: {
      questionId: number;
      questionText: string;
      questionType: string;
      textAnswer: string | null;
      ratingValue: number | null;
      chosenOptions: string | null;
    }[];
  }[];
  results: SurveyQuestionResult[];
}

export interface SurveyAnswerPayload {
  questionId: number;
  optionId?: number;
  optionIds?: number[];
  textAnswer?: string;
  ratingValue?: number;
}

export async function apiGetEmployeeSurveys(): Promise<Survey[]> {
  return apiFetch("/api/surveys?scope=mine");
}

export async function apiGetSurveys(): Promise<Survey[]> {
  return apiFetch("/api/surveys");
}
export async function apiGetSurvey(id: number, scope?: "mine"): Promise<SurveyDetail> {
  return apiFetch(`/api/surveys/${id}${scope === "mine" ? "?scope=mine" : ""}`);
}
export async function apiCreateSurvey(data: {
  title: string;
  description?: string;
  isAnonymous?: boolean;
  startDate?: string;
  endDate?: string;
}): Promise<{ id: number }> {
  return apiFetch("/api/surveys", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiUpdateSurvey(
  id: number,
  data: Partial<{
    title: string;
    description: string;
    isAnonymous: boolean;
    status: string;
    startDate: string;
    endDate: string;
  }>,
): Promise<{ id: number }> {
  return apiFetch(`/api/surveys/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteSurvey(
  id: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/surveys/${id}`, { method: "DELETE" });
}
export async function apiPublishSurvey(
  id: number,
): Promise<{ published: boolean; notified: number }> {
  return apiFetch(`/api/surveys/${id}/publish`, { method: "POST" });
}
export async function apiCloseSurvey(id: number): Promise<{ closed: boolean }> {
  return apiFetch(`/api/surveys/${id}/close`, { method: "POST" });
}
export async function apiAddSurveyQuestion(
  surveyId: number,
  data: {
    questionText: string;
    questionType?: string;
    isRequired?: boolean;
    sortOrder?: number;
    options?: { optionText: string }[];
  },
): Promise<SurveyQuestion> {
  return apiFetch(`/api/surveys/${surveyId}/questions`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiUpdateSurveyQuestion(
  surveyId: number,
  qId: number,
  data: Partial<{
    questionText: string;
    questionType: string;
    isRequired: boolean;
    sortOrder: number;
    options: { optionText: string }[];
  }>,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/surveys/${surveyId}/questions/${qId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteSurveyQuestion(
  surveyId: number,
  qId: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/surveys/${surveyId}/questions/${qId}`, {
    method: "DELETE",
  });
}
export async function apiSubmitSurveyResponse(
  surveyId: number,
  answers: SurveyAnswerPayload[],
): Promise<{ success: boolean }> {
  return apiFetch(`/api/surveys/${surveyId}/respond`, {
    method: "POST",
    body: JSON.stringify({ answers }),
  });
}

export async function apiGetSurveyResults(
  surveyId: number,
): Promise<SurveyResults> {
  return apiFetch(`/api/surveys/${surveyId}/results`);
}
export async function apiSendSurveyReminder(
  surveyId: number,
): Promise<{ reminded: number }> {
  return apiFetch(`/api/surveys/${surveyId}/remind`, { method: "POST" });
}
export function getSurveyExportUrl(surveyId: number): string {
  const params = new URLSearchParams();
  const userId = getCurrentUserId();
  if (userId) params.set("uid", userId);
  params.set("x-tenant", getTenantSlug());
  return `${API_BASE}/api/surveys/${surveyId}/export?${params.toString()}`;
}

export async function apiExportSurvey(surveyId: number, surveyTitle?: string): Promise<void> {
  const tenant = getTenantSlug();
  const token = getStoredToken();
  const headers: Record<string, string> = {
    "x-tenant": tenant,
    ...(token ? { "Authorization": `Bearer ${token}` } : {}),
  };
  const qs = new URLSearchParams({ "_t": tenant });
  const response = await fetch(`${API_BASE}/api/surveys/${surveyId}/export?${qs}`, { headers });
  if (!response.ok) throw new Error("Export failed");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeTitle = (surveyTitle ?? `survey-${surveyId}`).replace(/[^a-z0-9]/gi, "-").toLowerCase();
  a.download = `${safeTitle}-export.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ==== Offboarding ====

export interface OffboardingRequest {
  id: number;
  employee_id: number;
  status: "pending_manager" | "pending_hr" | "active" | "completed" | "rejected" | "revoked";
  preferred_last_day: string;
  actual_last_day: string | null;
  reason: string | null;
  manager_notes: string | null;
  hr_notes: string | null;
  revocation_reason: string | null;
  created_at: string;
  updated_at: string;

  // Joined fields
  employee_name?: string;
  employee_job_title?: string;
  employee_email?: string;
}

export async function apiGetOffboardingRequests(): Promise<OffboardingRequest[]> {
  return apiFetch("/api/offboarding");
}

export async function apiGetMyOffboardingRequest(): Promise<OffboardingRequest | null> {
  return apiFetch("/api/offboarding/my-requests");
}

export async function apiGetTeamOffboardingRequests(): Promise<OffboardingRequest[]> {
  return apiFetch("/api/offboarding/team");
}

export async function apiCreateOffboardingRequest(
  employee_id: number,
  preferred_last_day: string,
  reason: string,
): Promise<OffboardingRequest> {
  return apiFetch("/api/offboarding/request", {
    method: "POST",
    body: JSON.stringify({ employee_id, preferred_last_day, reason }),
  });
}

export async function apiAcknowledgeOffboardingRequest(
  request_id: number,
  manager_notes: string,
  action: "approve" | "reject"
): Promise<OffboardingRequest> {
  return apiFetch("/api/offboarding/acknowledge", {
    method: "PATCH",
    body: JSON.stringify({ request_id, manager_notes, action }),
  });
}

export async function apiRevokeOffboardingRequest(
  request_id: number,
  revocation_reason?: string,
): Promise<OffboardingRequest> {
  return apiFetch("/api/offboarding/revoke", {
    method: "POST",
    body: JSON.stringify({ request_id, revocation_reason }),
  });
}


export async function apiRejectOffboardingHR(
  request_id: number,
  hr_notes: string
): Promise<OffboardingRequest> {
  return apiFetch("/api/offboarding/reject-hr", {
    method: "POST",
    body: JSON.stringify({ request_id, hr_notes }),
  });
}

export async function apiCancelOffboardingHR(
  request_id: number,
  revocation_reason?: string,
): Promise<OffboardingRequest> {
  return apiFetch("/api/offboarding/cancel-hr", {
    method: "POST",
    body: JSON.stringify({ request_id, revocation_reason }),
  });
}

export async function apiEditOffboardingDates(
  request_id: number,
  actual_last_day: string,
  hr_notes?: string,
): Promise<OffboardingRequest> {
  return apiFetch("/api/offboarding/edit-dates", {
    method: "PATCH",
    body: JSON.stringify({ request_id, actual_last_day, hr_notes }),
  });
}

export async function apiFinalizeOffboardingRequest(
  request_id: number,
  actual_last_day: string,
  hr_notes: string,
  status: "active" | "completed",
): Promise<OffboardingRequest> {
  return apiFetch("/api/offboarding/finalize", {
    method: "PATCH",
    body: JSON.stringify({ request_id, actual_last_day, hr_notes, status }),
  });
}

// ── Settings ──────────────────────────────────────────────────────────────────

export interface NotificationPrefs {
  [type: string]: { inApp: boolean; email: boolean };
}

export interface Reminder {
  id: number;
  title: string;
  description: string | null;
  dueDate: string | null;
  category: string;
  repeat: string;
  isDone: boolean;
  createdAt: string;
}

// Notification preferences
export async function apiGetNotificationPrefs(): Promise<NotificationPrefs> {
  return apiFetch("/api/settings/notification-prefs");
}
export async function apiUpdateNotificationPrefs(
  prefs: { type: string; inApp: boolean; email: boolean }[],
): Promise<void> {
  return apiFetch("/api/settings/notification-prefs", {
    method: "PUT",
    body: JSON.stringify(prefs),
  });
}

// Reminders
export async function apiGetReminders(): Promise<Reminder[]> {
  return apiFetch("/api/settings/reminders");
}
export async function apiCreateReminder(data: {
  title: string;
  description?: string;
  dueDate?: string;
  category?: string;
  repeat?: string;
}): Promise<Reminder> {
  return apiFetch("/api/settings/reminders", {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiUpdateReminder(
  id: number,
  data: Partial<{
    title: string;
    description: string;
    dueDate: string;
    category: string;
    repeat: string;
    isDone: boolean;
  }>,
): Promise<Reminder> {
  return apiFetch(`/api/settings/reminders/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteReminder(id: number): Promise<void> {
  return apiFetch(`/api/settings/reminders/${id}`, { method: "DELETE" });
}

// Password change
export async function apiChangePassword(data: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  return apiFetch("/api/settings/change-password", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ── Two-Factor Authentication ─────────────────────────────────────────────────

export async function apiGet2FAStatus(): Promise<{ enabled: boolean }> {
  return apiFetch("/api/settings/2fa/status");
}

export async function apiSetup2FA(): Promise<{ secret: string; qrUri: string }> {
  return apiFetch("/api/settings/2fa/setup", { method: "POST" });
}

export async function apiEnable2FA(secret: string, code: string): Promise<void> {
  return apiFetch("/api/settings/2fa/enable", {
    method: "POST",
    body: JSON.stringify({ secret, code }),
  });
}

export async function apiDisable2FA(password: string): Promise<void> {
  return apiFetch("/api/settings/2fa/disable", {
    method: "DELETE",
    body: JSON.stringify({ password }),
  });
}

// ── GDPR ─────────────────────────────────────────────────────────────────────

export async function apiExportMyData(): Promise<void> {
  const token = getStoredToken();
  const tenantSlug = getTenantSlug();
  const sep = "/api/gdpr/my-data".includes("?") ? "&" : "?";
  const cacheSafePath = `/api/gdpr/my-data${sep}_t=${encodeURIComponent(tenantSlug)}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "x-tenant": tenantSlug,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const res = await fetch(`${API_BASE}${cacheSafePath}`, { cache: "no-store", headers });
  if (!res.ok) throw new Error("Failed to export data");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "GDPR_Data_Export.pdf";
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function apiAnonymiseEmployee(employeeId: string): Promise<void> {
  return apiFetch(`/api/gdpr/anonymise/${employeeId}`, { method: "POST" });
}

// ── Nav Visibility Permissions ────────────────────────────────────────────────

export interface NavVisibilitySetting {
  id: number;
  navKey: string;
  section: "administration" | "teamManagement";
  visibleToAdmin: boolean;
  visibleToHr: boolean;
  visibleToUser: boolean;
  visibleToLineManager: boolean;
  visibleToCLevel: boolean;
}

export async function apiGetNavPermissions(): Promise<NavVisibilitySetting[]> {
  return apiFetch("/api/nav-permissions");
}

export async function apiUpdateNavPermissions(
  items: Omit<NavVisibilitySetting, "id">[],
): Promise<NavVisibilitySetting[]> {
  return apiFetch("/api/nav-permissions", {
    method: "PUT",
    body: JSON.stringify(items),
  });
}

// ── Teams ────────────────────────────────────────────────────────────────────

export interface TeamMemberItem {
  id: string;
  name: string;
  initials: string;
  jobTitle: string;
  avatarColor: string;
  avatarUrl: string | null;
  isLeader: boolean;
}

export interface MyTeamResponse {
  teamName: string | null;
  isLeader: boolean;
  members: TeamMemberItem[];
}

export interface JoinRequest {
  id: number;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  requester: { id: string; name: string; jobTitle: string };
  target: { id: string; name: string; jobTitle: string };
  currentLm: { id: string; name: string } | null;
}

export async function apiGetMyTeam(): Promise<MyTeamResponse> {
  return apiFetch("/api/teams/my-team");
}

export async function apiGetTeamNames(): Promise<string[]> {
  return apiFetch("/api/teams/names");
}

export async function apiGetJoinRequests(): Promise<JoinRequest[]> {
  return apiFetch("/api/teams/join-requests");
}

export async function apiCreateJoinRequest(targetId: string): Promise<{ id: number }> {
  return apiFetch("/api/teams/join-requests", {
    method: "POST",
    body: JSON.stringify({ targetId }),
  });
}

export async function apiApproveJoinRequest(id: number): Promise<void> {
  return apiFetch(`/api/teams/join-requests/${id}/approve`, { method: "PUT" });
}

export async function apiRejectJoinRequest(id: number): Promise<void> {
  return apiFetch(`/api/teams/join-requests/${id}/reject`, { method: "PUT" });
}

// ---- Team Skills & Allocations ----
export async function apiUpdateTeamSkills(
  draft: Record<number, Record<string, number>>
): Promise<{ success: boolean }> {
  return apiFetch("/api/teams/skills", {
    method: "PUT",
    body: JSON.stringify(draft),
  });
}

export async function apiUpdateTeamAllocations(
  employeeId: number,
  allocations: { projectId: string; projectName: string; percent: number }[]
): Promise<{ success: boolean }> {
  return apiFetch("/api/teams/allocations", {
    method: "PUT",
    body: JSON.stringify({ employeeId, allocations }),
  });
}

// ════════════════════════════════════════════════════════════════════
// Workflow Engine
// ════════════════════════════════════════════════════════════════════

export interface TriggerConditionRule {
  field: string;
  operator: string;
  value: string;
}

export interface TriggerCondition {
  combinator: "AND" | "OR";
  rules: TriggerConditionRule[];
}

export interface WorkflowDefinition {
  id: number;
  name: string;
  description: string | null;
  trigger_event: string;
  is_active: boolean;
  trigger_condition: TriggerCondition | null;
  created_by: number | null;
  created_by_name?: string;
  active_instances?: number;
  created_at: string;
  updated_at: string;
}

export type WorkflowStepType =
  | "approval"
  | "task"
  | "service_action"
  | "notification"
  | "condition"
  | "parallel"
  | "merge"
  | "delay"
  | "switch"
  | "end";

export interface WorkflowStep {
  id: number;
  workflow_def_id: number;
  step_order: number;
  name: string;
  step_type: WorkflowStepType;
  assign_to_role: string | null;
  assign_to_user_id: number | null;
  config: Record<string, any>;
  /** Canvas X coordinate. Null for workflows created before the canvas (engine ignores). */
  position_x: number | null;
  position_y: number | null;
  /** How many output handles this node has. 1 for linear, 2 for condition, N for switch/parallel. */
  outputs_count: number;
  created_at: string;
}

export interface WorkflowEdge {
  id: number;
  workflow_def_id: number;
  source_step_id: number;
  /** 'out' (linear) | 'true' | 'false' | 'case_<n>' | 'branch_<n>'. */
  source_handle: string;
  target_step_id: number;
}

export interface WorkflowDefinitionDetail extends WorkflowDefinition {
  steps: WorkflowStep[];
  edges: WorkflowEdge[];
}

export interface WorkflowInstance {
  id: number;
  workflow_def_id: number;
  workflow_name: string;
  trigger_event: string;
  entity_type: string;
  entity_id: number | null;
  context: Record<string, any>;
  status: "active" | "completed" | "cancelled" | "failed";
  current_step_id: number | null;
  current_step_name?: string;
  initiated_by: number | null;
  initiated_by_name?: string;
  created_at: string;
  completed_at: string | null;
  updated_at: string;
  // HR owner fields — set when an instance has been claimed (or auto-assigned)
  // and routes hr-role tasks/recipients exclusively to that user. When
  // hr_owner_user_id IS NULL AND owner_pending_since IS NOT NULL the instance
  // is paused before step 1 awaiting an HR colleague to claim it.
  hr_owner_user_id?: number | null;
  hr_owner_name?: string | null;
  owner_pending_since?: string | null;
  owner_auto_assigned?: boolean;
}

export interface WorkflowInstanceDetail extends WorkflowInstance {
  executions: {
    id: number;
    step_name: string;
    step_type: string;
    step_order: number;
    status: string;
    outcome: string | null;
    acted_by: number | null;
    acted_by_name?: string;
    notes: string | null;
    started_at: string | null;
    completed_at: string | null;
  }[];
}

export interface WorkflowTask {
  id: number;
  execution_id: number;
  instance_id: number;
  assigned_to_user_id: number;
  title: string;
  description: string | null;
  task_type: string;
  status: "pending" | "completed" | "cancelled";
  outcome?: string | null;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  is_archived?: boolean;
  workflow_name?: string;
  entity_type?: string;
  context?: Record<string, any>;
  step_name?: string;
  step_type?: string;
}

export interface TriggerConditionField {
  field: string;
  label: string;
  type: "string" | "number";
  optionsSource?: "departments" | "leaveTypes" | "employees" | "currencies" | "teams";
}

export interface WorkflowTrigger {
  event: string;
  label: string;
  entityType: string;
  conditionFields: TriggerConditionField[];
}

// Definitions
export async function apiGetWorkflowDefinitions(): Promise<WorkflowDefinition[]> {
  return apiFetch("/api/workflows/definitions");
}

export async function apiGetWorkflowDefinition(id: number): Promise<WorkflowDefinitionDetail> {
  return apiFetch(`/api/workflows/definitions/${id}`);
}

export async function apiCreateWorkflowDefinition(data: {
  name: string; description?: string; trigger_event: string; trigger_condition?: TriggerCondition | null;
}): Promise<WorkflowDefinition> {
  return apiFetch("/api/workflows/definitions", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateWorkflowDefinition(
  id: number,
  data: Partial<{ name: string; description: string; trigger_event: string; is_active: boolean; trigger_condition: TriggerCondition | null }>,
): Promise<WorkflowDefinition> {
  return apiFetch(`/api/workflows/definitions/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteWorkflowDefinition(id: number): Promise<void> {
  return apiFetch(`/api/workflows/definitions/${id}`, { method: "DELETE" });
}

// Steps
export async function apiAddWorkflowStep(defId: number, data: {
  name: string; step_type: string; assign_to_role?: string; assign_to_user_id?: number;
  config?: Record<string, any>;
  position_x?: number; position_y?: number; outputs_count?: number;
}): Promise<WorkflowStep> {
  return apiFetch(`/api/workflows/definitions/${defId}/steps`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiUpdateWorkflowStep(
  defId: number, stepId: number, data: Partial<WorkflowStep>,
): Promise<WorkflowStep> {
  return apiFetch(`/api/workflows/definitions/${defId}/steps/${stepId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteWorkflowStep(defId: number, stepId: number): Promise<void> {
  return apiFetch(`/api/workflows/definitions/${defId}/steps/${stepId}`, { method: "DELETE" });
}

export async function apiReorderWorkflowSteps(defId: number, stepIds: number[]): Promise<void> {
  return apiFetch(`/api/workflows/definitions/${defId}/steps/reorder`, {
    method: "PUT",
    body: JSON.stringify({ stepIds }),
  });
}

// Lightweight position update for canvas drag events — fires per node on drop.
export async function apiUpdateStepPosition(
  stepId: number, x: number, y: number,
): Promise<{ id: number; position_x: number; position_y: number }> {
  return apiFetch(`/api/workflows/steps/${stepId}/position`, {
    method: "PATCH",
    body: JSON.stringify({ x, y }),
  });
}

// Edges
export async function apiAddWorkflowEdge(
  defId: number,
  data: { source_step_id: number; source_handle?: string; target_step_id: number },
): Promise<WorkflowEdge> {
  return apiFetch(`/api/workflows/definitions/${defId}/edges`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteWorkflowEdge(edgeId: number): Promise<void> {
  return apiFetch(`/api/workflows/edges/${edgeId}`, { method: "DELETE" });
}

// Replace all edges for a workflow in one call — used by canvas save-all.
export async function apiReplaceWorkflowEdges(
  defId: number,
  edges: Array<{ source_step_id: number; source_handle?: string; target_step_id: number }>,
): Promise<WorkflowEdge[]> {
  return apiFetch(`/api/workflows/definitions/${defId}/edges`, {
    method: "PUT",
    body: JSON.stringify({ edges }),
  });
}

// Instances
export async function apiGetWorkflowInstances(status?: string): Promise<WorkflowInstance[]> {
  const qs = status ? `?status=${status}` : "";
  return apiFetch(`/api/workflows/instances${qs}`);
}

export async function apiGetWorkflowInstance(id: number): Promise<WorkflowInstanceDetail> {
  return apiFetch(`/api/workflows/instances/${id}`);
}

export async function apiCancelWorkflowInstance(id: number): Promise<void> {
  return apiFetch(`/api/workflows/instances/${id}/cancel`, { method: "POST" });
}

// HR owner ─────────────────────────────────────────────────────────────────
// claim → caller becomes owner (idempotent). 409 if a different HR owns it.
// set   → reassign to a specific HR/admin user (any HR/admin can do this).
// pending → list instances awaiting an owner (for the "needs attention" callout).
export async function apiClaimWorkflowOwner(
  instanceId: number,
): Promise<{ success: true; alreadyOwner: boolean; advanceWarning: string | null }> {
  return apiFetch(`/api/workflows/instances/${instanceId}/claim-owner`, { method: "POST" });
}

export async function apiSetWorkflowOwner(
  instanceId: number,
  userId: number,
): Promise<{ success: true }> {
  return apiFetch(`/api/workflows/instances/${instanceId}/owner`, {
    method: "PUT",
    body: JSON.stringify({ userId }),
  });
}

export async function apiGetPendingOwnerInstances(): Promise<WorkflowInstance[]> {
  return apiFetch(`/api/workflows/instances/pending-owner`);
}

export interface HrOwnerCandidate {
  id: number;
  name: string;
  email: string;
  main_role: "hr" | "admin";
  active_owned_count: number;
}

export async function apiGetHrOwnerCandidates(): Promise<HrOwnerCandidate[]> {
  return apiFetch(`/api/workflows/hr-owner-candidates`);
}

export async function apiRerunWorkflowStep(
  instanceId: number,
  executionId: number,
): Promise<{ success: boolean; message: string }> {
  return apiFetch(`/api/workflows/instances/${instanceId}/rerun-step/${executionId}`, {
    method: "POST",
  });
}

export interface WorkflowAuditEntry {
  id: number;
  event_type: string;
  step_execution_id: number | null;
  details: Record<string, any> | null;
  created_at: string;
  user_name: string | null;
}

export async function apiGetWorkflowInstanceAuditLog(
  instanceId: number,
): Promise<WorkflowAuditEntry[]> {
  return apiFetch(`/api/workflows/instances/${instanceId}/audit-log`);
}

// Tasks
export async function apiGetMyWorkflowTasks(): Promise<WorkflowTask[]> {
  return apiFetch("/api/workflows/tasks");
}

export async function apiGetWorkflowTaskCount(): Promise<{ count: number }> {
  return apiFetch("/api/workflows/tasks/count");
}

// Action-required counts for sidebar badges
export async function apiGetPendingSurveysCount(): Promise<{ count: number }> {
  return apiFetch("/api/surveys/pending-count");
}
export async function apiGetPendingOnboardingCount(): Promise<{ count: number }> {
  return apiFetch("/api/onboarding/pending-count");
}
export async function apiGetPendingDocumentsCount(): Promise<{ count: number }> {
  return apiFetch("/api/documents/pending-count");
}
export async function apiGetPendingLeaveCount(): Promise<{ count: number }> {
  return apiFetch("/api/leave/pending-count");
}
export async function apiGetUnreadNotificationsCount(): Promise<{ count: number }> {
  return apiFetch("/api/notifications/unread-count");
}
export async function apiGetPendingOffboardingCount(): Promise<{ count: number }> {
  return apiFetch("/api/offboarding/pending-count");
}
export async function apiGetMyPendingExpensesCount(): Promise<{ count: number }> {
  return apiFetch("/api/expenses/my-pending-count");
}

export async function apiGetAdminPendingExpensesCount(): Promise<{ count: number }> {
  return apiFetch("/api/expenses/admin-pending-count");
}

export async function apiGetPendingRecruitmentCount(): Promise<{ count: number }> {
  return apiFetch("/api/recruitment/applications/pending-count");
}

export async function apiGetPendingPayrollCount(): Promise<{ count: number }> {
  return apiFetch("/api/payroll/pending-count");
}

export async function apiGetAdminPendingOnboardingCount(): Promise<{ count: number }> {
  return apiFetch("/api/onboarding/admin-pending-count");
}

export interface SubmittedTask {
  id: number;
  title: string;
  description: string | null;
  status: "pending" | "completed" | "cancelled";
  outcome: "approved" | "rejected" | null;
  created_at: string;
  completed_at: string | null;
  task_context: {
    employeeName?: string;
    baseSalary?: number;
    currency?: string;
    reason?: string;
  } | null;
}

export async function apiGetMySubmittedTasks(): Promise<SubmittedTask[]> {
  return apiFetch("/api/workflows/tasks/submitted");
}

export async function apiArchiveSubmittedTask(taskId: number): Promise<void> {
  return apiFetch(`/api/workflows/tasks/${taskId}/archive-submitted`, { method: "POST" });
}

export async function apiDeleteSubmittedTask(taskId: number): Promise<void> {
  return apiFetch(`/api/workflows/tasks/${taskId}/submitted`, { method: "DELETE" });
}

export async function apiCompleteWorkflowTask(
  taskId: number, data: { outcome: string; notes?: string },
): Promise<void> {
  return apiFetch(`/api/workflows/tasks/${taskId}/complete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface TaskEmployeeCard {
  id: string;
  name: string;
  initials: string;
  avatarColor: string;
  avatarUrl: string | null;
  jobTitle: string | null;
  department: string | null;
  teamName: string | null;
  contractType: string | null;
  workSchedule: string | null;
  startDate: string | null;
  status: string;
  reportsTo: { id: string; name: string; initials: string; avatarColor: string; avatarUrl: string | null } | null;
  equipment: { item_name: string; serial_number: string | null; status: string }[];
  canSeeSalary: boolean;
  salary: { base_salary: number; currency: string; pay_frequency: string; bonus: number | null } | null;
}

export async function apiGetTaskEmployeeCard(taskId: number): Promise<TaskEmployeeCard> {
  return apiFetch(`/api/workflows/tasks/${taskId}/employee-card`);
}

export async function apiArchiveWorkflowTask(taskId: number): Promise<void> {
  return apiFetch(`/api/workflows/tasks/${taskId}/archive`, { method: "POST" });
}

export async function apiUnarchiveWorkflowTask(taskId: number): Promise<void> {
  return apiFetch(`/api/workflows/tasks/${taskId}/unarchive`, { method: "POST" });
}

export async function apiGetArchivedWorkflowTasks(): Promise<WorkflowTask[]> {
  return apiFetch("/api/workflows/tasks/archived");
}

export async function apiUnarchiveSubmittedTask(taskId: number): Promise<void> {
  return apiFetch(`/api/workflows/tasks/${taskId}/unarchive-submitted`, { method: "POST" });
}

export async function apiGetArchivedSubmittedTasks(): Promise<SubmittedTask[]> {
  return apiFetch("/api/workflows/tasks/archived-submitted");
}

export async function apiDeleteWorkflowTask(taskId: number): Promise<void> {
  return apiFetch(`/api/workflows/tasks/${taskId}`, { method: "DELETE" });
}

// Triggers
export async function apiGetWorkflowTriggers(): Promise<WorkflowTrigger[]> {
  return apiFetch("/api/workflows/triggers");
}

// Trigger options (DB-backed dropdown values per optionsSource)
export interface TriggerOptions {
  departments: { value: string; label: string }[];
  leaveTypes:  { value: string; label: string }[];
  employees:   { value: string; label: string }[];
  currencies:  { value: string; label: string }[];
  teams?:      { value: string; label: string }[];
}

export async function apiGetWorkflowTriggerOptions(): Promise<TriggerOptions> {
  return apiFetch("/api/workflows/trigger-options");
}


// Salary increase request
export async function apiRequestSalaryIncrease(data: {
  employeeId: number; baseSalary: number; currency?: string;
  payFrequency?: string; bonus?: number; effectiveDate?: string; reason?: string;
}): Promise<{ ok: boolean; message: string }> {
  return apiFetch("/api/workflows/salary-increase-request", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---- Raise Concern (Anonymous Reporting) ----

export interface RaiseConcernMessage {
  id: string;
  sender: "hr" | "submitter";
  kind?: "message" | "case_cancelled" | "review_requested";
  text: string;
  timestamp: string;
  metadata?: {
    reason?: string | null;
    previousStatus?: string | null;
  };
}

export type RaiseConcernStatus =
  | "submitted"
  | "in_review"
  | "assigned"
  | "resolved"
  | "closed"
  | "cancelled";

/**
 * Employee's view of their own submitted case.
 * submitted_by is intentionally absent — the backend never returns it.
 */
export interface RaiseConcernCase {
  id: number;
  status: RaiseConcernStatus;
  description: string;
  hrFeedback: string | null;
  createdAt: string;
  updatedAt: string;
  reportedEmployeeName: string;
  reportedEmployeeTitle: string | null;
  assignedToName: string | null;
}

/**
 * HR's view of a case.
 * submitted_by is intentionally absent — the backend enforces this.
 * hr_notes is included because HR is allowed to see internal notes.
 */
export interface RaiseConcernCaseHR extends RaiseConcernCase {
  hrNotes: string | null;
  reportedEmployeeDept: string | null;
  assignedToId: number | null;
}

export interface RaiseConcernOpenCount {
  openCount: number;
  newCount: number;
  inReviewCount: number;
  assignedCount: number;
}

/** Employee: submit a new anonymous concern */
export async function apiSubmitConcern(data: {
  reportedEmployeeId: number;
  description: string;
}): Promise<{ id: number; status: string; createdAt: string }> {
  return apiFetch("/api/raise-concern", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/** Employee: get their own submitted cases */
export async function apiGetMyConcernCases(): Promise<RaiseConcernCase[]> {
  return apiFetch("/api/raise-concern/my-cases");
}

/** HR: get all cases (no submitted_by returned by backend) */
export async function apiGetAllConcernCases(status?: string): Promise<RaiseConcernCaseHR[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch(`/api/raise-concern${qs}`);
}

/** HR: get count of open cases (for dashboard widget) */
export async function apiGetConcernOpenCount(): Promise<RaiseConcernOpenCount> {
  return apiFetch("/api/raise-concern/open-count");
}

/** HR: update case status */
export async function apiUpdateConcernStatus(
  id: number,
  status: RaiseConcernStatus,
): Promise<{ id: number; status: RaiseConcernStatus }> {
  return apiFetch(`/api/raise-concern/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

/** HR: assign case to an employee */
export async function apiAssignConcernCase(
  id: number,
  assignedToEmployeeId: number | null,
): Promise<{ id: number; status: string }> {
  return apiFetch(`/api/raise-concern/${id}/assign`, {
    method: "PATCH",
    body: JSON.stringify({ assignedToEmployeeId }),
  });
}

/** HR: save internal notes (never visible to submitter) */
export async function apiUpdateConcernNotes(
  id: number,
  hrNotes: string,
): Promise<{ id: number; success: boolean }> {
  return apiFetch(`/api/raise-concern/${id}/notes`, {
    method: "PATCH",
    body: JSON.stringify({ hrNotes }),
  });
}

/** HR: publish feedback visible to the anonymous submitter */
export async function apiUpdateConcernFeedback(
  id: number,
  hrFeedback: string,
): Promise<{ id: number; success: boolean }> {
  return apiFetch(`/api/raise-concern/${id}/feedback`, {
    method: "PATCH",
    body: JSON.stringify({ hrFeedback }),
  });
}

/** Employee: reply to HR inside a concern thread */
export async function apiReplyToConcern(
  id: number,
  text: string,
): Promise<{ id: number; success: boolean }> {
  return apiFetch(`/api/raise-concern/${id}/feedback`, {
    method: "PATCH",
    body: JSON.stringify({ text }),
  });
}

export async function apiCancelConcern(
  id: number,
  reason?: string,
): Promise<{ id: number; status: RaiseConcernStatus; success: boolean }> {
  return apiFetch(`/api/raise-concern/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function apiRequestConcernReview(
  id: number,
  message?: string,
): Promise<{ id: number; status: RaiseConcernStatus; success: boolean }> {
  return apiFetch(`/api/raise-concern/${id}/request-review`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}

// ── UI Preferences ─────────────────────────────────────────────────────────────

export async function apiGetUiPrefs(): Promise<Record<string, string>> {
  return apiFetch("/api/settings/ui-prefs");
}

export async function apiSetUiPref(key: string, value: string): Promise<{ ok: boolean }> {
  return apiFetch("/api/settings/ui-prefs", {
    method: "PUT",
    body: JSON.stringify({ key, value }),
  });
}

// ── Salary Configuration ────────────────────────────────────────────────────

export interface SalaryTaxRule {
  id: number;
  ruleKey: string;
  label: string;
  valueType: "rate" | "fixed";
  value: number;
  effectiveFrom: string;
  createdAt?: string;
  createdBy?: number;
  createdByName?: string;
}

export interface SalaryConfig {
  id: number;
  countryCode: string;
  countryName: string;
  currency: string;
  averageSalary: number;
  maxBaseMultiplier: number;
  minNet: number;
  minGross: number;
}

export interface SalaryConfigData {
  config: SalaryConfig;
  rules: SalaryTaxRule[];
}

export interface CountryPreset {
  code: string;
  name: string;
  currency: string;
}

export interface AffectedDraftRun {
  id: number;
  month: number;
  year: number;
}

export async function apiGetSalaryConfig(): Promise<SalaryConfigData> {
  return apiFetch("/api/salary-config");
}

export async function apiGetCountryPresets(): Promise<CountryPreset[]> {
  return apiFetch("/api/salary-config/countries");
}

export async function apiGetCompanyCurrency(): Promise<{ currency: string }> {
  return apiFetch("/api/salary-config/currency");
}

export async function apiSetSalaryCountry(countryCode: string): Promise<{ ok: boolean }> {
  return apiFetch("/api/salary-config/country", {
    method: "PUT",
    body: JSON.stringify({ countryCode }),
  });
}

export async function apiUpdateSalaryConfigParams(params: {
  averageSalary?: number;
  maxBaseMultiplier?: number;
  minNet?: number;
  minGross?: number;
}): Promise<{ ok: boolean }> {
  return apiFetch("/api/salary-config/params", {
    method: "PUT",
    body: JSON.stringify(params),
  });
}

export async function apiAddTaxRule(data: {
  ruleKey: string;
  label: string;
  valueType: string;
  value: number;
  effectiveFrom: string;
}): Promise<{ rule: SalaryTaxRule; affectedDraftRuns: AffectedDraftRun[] }> {
  return apiFetch("/api/salary-config/rules", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiGetTaxRuleHistory(): Promise<SalaryTaxRule[]> {
  return apiFetch("/api/salary-config/rules/history");
}

export async function apiRecalculatePayrollRun(runId: number): Promise<{ ok: boolean; recalculated: number }> {
  return apiFetch(`/api/salary-config/recalculate-run/${runId}`, { method: "POST" });
}

// ── Platform Audit Log ──────────────────────────────────────────────────────

export interface PlatformAuditLogEntry {
  id: number;
  userId: number | null;
  userName: string;
  userEmail: string | null;
  action: string;
  module: string;
  message: string;
  metadata: Record<string, any> | null;
  createdAt: string;
}

export interface AuditLogPage {
  rows: PlatformAuditLogEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface AuditLogStats {
  total: number;
  today: number;
  byModule: { module: string; count: number }[];
  byAction: { action: string; count: number }[];
  topUsers:  { name: string; email: string; count: number }[];
}

function buildAuditLogQS(params: {
  page?: number; limit?: number; modules?: string[]; userId?: number;
  actions?: string[]; search?: string; from?: string; to?: string;
}): URLSearchParams {
  const qs = new URLSearchParams();
  if (params.page)                  qs.set("page",   String(params.page));
  if (params.limit)                 qs.set("limit",  String(params.limit));
  if (params.modules?.length)       qs.set("module", params.modules.join(","));
  if (params.userId)                qs.set("userId", String(params.userId));
  if (params.actions?.length)       qs.set("action", params.actions.join(","));
  if (params.search)                qs.set("search", params.search);
  if (params.from)                  qs.set("from",   params.from);
  if (params.to)                    qs.set("to",     params.to);
  return qs;
}

export async function apiGetAuditLog(params: {
  page?: number; limit?: number; modules?: string[]; userId?: number;
  actions?: string[]; search?: string; from?: string; to?: string;
}): Promise<AuditLogPage> {
  return apiFetch(`/api/audit-log?${buildAuditLogQS(params)}`);
}

export async function apiExportAuditLog(params: {
  modules?: string[]; actions?: string[]; search?: string; from?: string; to?: string;
}): Promise<void> {
  const qs = buildAuditLogQS(params);
  const userId = getCurrentUserId();
  const tenant = getTenantSlug();
  qs.set("_t", tenant);
  const headers: Record<string, string> = {
    "x-tenant": tenant,
    ...(userId ? { "x-user-id": userId } : {}),
  };
  const response = await fetch(`/api/audit-log/export?${qs}`, { headers });
  if (!response.ok) throw new Error("Export failed");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function apiGetAuditLogModules(): Promise<string[]> {
  return apiFetch("/api/audit-log/modules");
}

export async function apiGetAuditLogStats(): Promise<AuditLogStats> {
  return apiFetch("/api/audit-log/stats");
}

// ── Superadmin: per-account audit log types ────────────────────────────────────
// Fetching is done via useSuperAdminFetch (Bearer token), not the tenant apiFetch.

export interface SuperadminPlatformAuditPage extends AuditLogPage {
  modules: string[];
}

export interface FieldAuditLogEntry {
  id: number;
  employee_id: number;
  employee_name: string | null;
  section: string;
  field_name: string | null;
  action: string;
  old_value: string | null;
  new_value: string | null;
  changed_by_email: string | null;
  changed_at: string;
}

export interface FieldAuditLogPage {
  logs: FieldAuditLogEntry[];
  total: number;
  page: number;
  limit: number;
}

// ── Recruitment ───────────────────────────────────────────────────────────────

// Public fetch helper — omits x-user-id but still sends x-tenant
async function apiFetchPublic<T>(path: string, options?: RequestInit): Promise<T> {
  const tenantSlug = getTenantSlug();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "x-tenant": tenantSlug,
  };
  const sep = path.includes("?") ? "&" : "?";
  const cacheSafePath = `${path}${sep}_t=${encodeURIComponent(tenantSlug)}`;
  const res = await fetch(`${API_BASE}${cacheSafePath}`, {
    ...options,
    cache: "no-store" as RequestCache,
    headers: { ...headers, ...((options?.headers as Record<string, string>) || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export interface RecruitmentKeyword {
  keyword: string;
  weight: 1 | 2 | 3;
}

export interface CvKeywordResult {
  keyword: string;
  weight: 1 | 2 | 3;
  found: boolean;
  confidence: number;
}

export interface CvScoreDetail {
  keywordResults: CvKeywordResult[];
  coverLetterScore: number;
  keywordScore: number;
  totalScore: number;
  summary?: string | null;
}

export interface RecruitmentPosition {
  id: number;
  title: string;
  departmentId: number | null;
  departmentName: string | null;
  description: string | null;
  requirements: string | null;
  location: string | null;
  jobType: string;
  status: string;
  publicSlug: string | null;
  iconName: string;
  termsAndConditions: string | null;
  privacyPolicy: string | null;
  keywords: RecruitmentKeyword[];
  applicationCount: number;
  applicationDeadline: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecruitmentDashboardStats {
  positionsByStatus: { status: string; count: number }[];
  totalPositions: number;
  totalOpenPositions: number;
  totalCandidates: number;
  newApplicationsLast7Days: number;
  applicationsByStatus: { status: string; count: number }[];
  applicationsPerPosition: {
    id: number;
    title: string;
    status: string;
    applicationCount: number;
    applicationDeadline: string | null;
  }[];
  positionsClosingSoon: {
    id: number;
    title: string;
    applicationDeadline: string;
    daysLeft: number;
    applicationCount: number;
  }[];
}

export interface RecruitmentCustomField {
  id: number;
  positionId: number;
  fieldLabel: string;
  fieldType: string;
  fieldOptions: string[];
  isRequired: boolean;
  sortOrder: number;
}

export interface RecruitmentApplication {
  id: number;
  positionId: number;
  positionTitle: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  resumeFileKey: string | null;
  coverLetter: string | null;
  linkedinUrl: string | null;
  portfolioUrl: string | null;
  status: string;
  notes: string | null;
  appliedAt: string;
  updatedAt: string;
  aiScore: number | null;
  aiScoreDetail: CvScoreDetail | null;
  aiAnalyzedAt: string | null;
  latestInterviewStatus: string | null;
  latestInterviewScheduledAt: string | null;
  latestInterviewerName: string | null;
  convertedEmployeeId: number | null;
  preboardingToken: string | null;
  preboardingTokenExpiresAt: string | null;
  preboardingCompletedAt: string | null;
}

export interface RecruitmentAnswer {
  id: number;
  fieldId: number;
  fieldLabel: string;
  fieldType: string;
  answerText: string | null;
  fileKey: string | null;
}

export interface RecruitmentInterview {
  id: number;
  assignedToEmployeeId: number | null;
  assignedToName: string | null;
  notes: string | null;
  status: string;
  scheduledAt: string | null;
  location: string | null;
  meetingLink: string | null;
  availabilitySentAt?: string | null;
  feedbackNotes: string | null;
  createdAt: string;
  bookingToken?: string | null;
  bookingTokenExpiresAt?: string | null;
  slotDurationMinutes?: number;
}

export interface RecruitmentApplicationDetail extends RecruitmentApplication {
  answers: RecruitmentAnswer[];
  interviews: RecruitmentInterview[];
}

export interface RecruitmentCandidateProfile {
  id: number;
  applicationId: number;
  skills: string[];
  technicalKnowledge: string | null;
  experienceSummary: string | null;
  personalityNotes: string | null;
  overallRating: number | null;
  recommendation: "strong_yes" | "yes" | "maybe" | "no" | "strong_no" | null;
  updatedByUserId: number | null;
  updatedAt: string;
  createdAt: string;
}

export interface RecruitmentInterviewScorecard {
  id: number;
  interviewId: number;
  communication: number | null;
  technical: number | null;
  problemSolving: number | null;
  cultureFit: number | null;
  motivation: number | null;
  strengths: string | null;
  weaknesses: string | null;
  overallRecommendation: "strong_yes" | "yes" | "maybe" | "no" | "strong_no" | null;
  submittedAt: string | null;
}

export interface RecruitmentProfileComment {
  id: number;
  applicationId: number;
  userId: number;
  userName: string;
  userRole: string;
  comment: string;
  createdAt: string;
}

export interface RecruitmentEmailTemplate {
  id: number;
  name: string;
  subject: string;
  bodyHtml: string;
  triggerStatus: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecruitmentPublicPosition {
  id: number;
  title: string;
  department: string | null;
  description: string | null;
  requirements: string | null;
  location: string | null;
  jobType: string;
  iconName: string;
  termsAndConditions: string | null;
  privacyPolicy: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
  customFields: RecruitmentCustomField[];
}

// Positions
export async function apiGetRecruitmentPositions(): Promise<RecruitmentPosition[]> {
  return apiFetch("/api/recruitment/positions");
}
export async function apiCreateRecruitmentPosition(data: {
  title: string;
  departmentId?: number | null;
  description?: string;
  requirements?: string;
  location?: string;
  jobType?: string;
  status?: string;
  keywords?: RecruitmentKeyword[];
  applicationDeadline?: string | null;
}): Promise<RecruitmentPosition> {
  return apiFetch("/api/recruitment/positions", { method: "POST", body: JSON.stringify(data) });
}
export async function apiUpdateRecruitmentPosition(
  id: number,
  data: Partial<{ title: string; departmentId: number | null; description: string; requirements: string; location: string; jobType: string; status: string; keywords: RecruitmentKeyword[]; applicationDeadline: string | null }>,
): Promise<RecruitmentPosition> {
  return apiFetch(`/api/recruitment/positions/${id}`, { method: "PUT", body: JSON.stringify(data) });
}
export async function apiGetRecruitmentDashboardStats(): Promise<RecruitmentDashboardStats> {
  return apiFetch("/api/recruitment/positions/dashboard-stats");
}
export async function apiAnalyzeApplication(id: number): Promise<RecruitmentApplication> {
  return apiFetch(`/api/recruitment/applications/${id}/analyze`, { method: "POST" });
}
export async function apiDeleteRecruitmentPosition(id: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/recruitment/positions/${id}`, { method: "DELETE" });
}

// Custom fields
export async function apiGeneratePositionContent(data: {
  field: "description" | "requirements";
  title: string;
  department?: string;
  location?: string;
  jobType?: string;
  language?: string;
}): Promise<{ text: string }> {
  return apiFetch("/api/recruitment/positions/generate-content", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiGetRecruitmentCustomFields(positionId: number): Promise<RecruitmentCustomField[]> {
  return apiFetch(`/api/recruitment/positions/${positionId}/custom-fields`);
}
export async function apiCreateRecruitmentCustomField(
  positionId: number,
  data: { fieldLabel: string; fieldType: string; fieldOptions?: string[]; isRequired?: boolean; sortOrder?: number },
): Promise<RecruitmentCustomField> {
  return apiFetch(`/api/recruitment/positions/${positionId}/custom-fields`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiUpdateRecruitmentCustomField(
  positionId: number,
  fieldId: number,
  data: Partial<{ fieldLabel: string; fieldType: string; fieldOptions: string[]; isRequired: boolean; sortOrder: number }>,
): Promise<RecruitmentCustomField> {
  return apiFetch(`/api/recruitment/positions/${positionId}/custom-fields/${fieldId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiDeleteRecruitmentCustomField(positionId: number, fieldId: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/recruitment/positions/${positionId}/custom-fields/${fieldId}`, { method: "DELETE" });
}
export async function apiReorderRecruitmentCustomFields(
  positionId: number,
  order: { id: number; sortOrder: number }[],
): Promise<{ success: boolean }> {
  return apiFetch(`/api/recruitment/positions/${positionId}/custom-fields/reorder`, {
    method: "PUT",
    body: JSON.stringify({ order }),
  });
}

// Applications
export async function apiGetRecruitmentApplications(params?: {
  positionId?: number;
  status?: string;
}): Promise<RecruitmentApplication[]> {
  const qs = new URLSearchParams();
  if (params?.positionId) qs.set("positionId", String(params.positionId));
  if (params?.status) qs.set("status", params.status);
  const q = qs.toString() ? `?${qs}` : "";
  return apiFetch(`/api/recruitment/applications${q}`);
}
export async function apiGetRecruitmentApplication(id: number): Promise<RecruitmentApplicationDetail> {
  return apiFetch(`/api/recruitment/applications/${id}`);
}
export async function apiCreateRecruitmentApplication(formData: FormData): Promise<RecruitmentApplication> {
  return apiMultipartFetch<RecruitmentApplication>("/api/recruitment/applications", formData);
}
export async function apiDeleteRecruitmentApplication(
  id: number,
  options?: { workflowTaskId?: number },
): Promise<{ success: boolean }> {
  const qs = new URLSearchParams();
  if (options?.workflowTaskId) qs.set("workflowTaskId", String(options.workflowTaskId));
  const q = qs.toString() ? `?${qs}` : "";
  return apiFetch(`/api/recruitment/applications/${id}${q}`, { method: "DELETE" });
}
export async function apiUpdateRecruitmentApplicationStatus(
  id: number,
  data: { status: string; templateId?: number },
): Promise<RecruitmentApplication> {
  return apiFetch(`/api/recruitment/applications/${id}/status`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiBulkUpdateRecruitmentApplicationStatus(
  ids: number[],
  status: string,
): Promise<{ updated: number }> {
  return apiFetch("/api/recruitment/applications/bulk-status", {
    method: "PUT",
    body: JSON.stringify({ ids, status }),
  });
}
export async function apiUpdateRecruitmentApplicationNotes(
  id: number,
  notes: string,
): Promise<RecruitmentApplication> {
  return apiFetch(`/api/recruitment/applications/${id}/notes`, {
    method: "PATCH",
    body: JSON.stringify({ notes }),
  });
}

// Interviews
export async function apiAssignRecruitmentInterview(
  applicationId: number,
  data: { employeeId: number; notes?: string },
): Promise<RecruitmentInterview> {
  return apiFetch(`/api/recruitment/applications/${applicationId}/interviews`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export async function apiScheduleInterview(
  applicationId: number,
  interviewId: number,
  data: { scheduledAt: string; location?: string; meetingLink?: string },
): Promise<{ id: number; status: string; scheduledAt: string; location: string | null; meetingLink: string | null }> {
  return apiFetch(`/api/recruitment/applications/${applicationId}/interviews/${interviewId}/schedule`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function apiHrInterviewDecision(
  applicationId: number,
  data: { decision: "offer" | "reject" | "another_round" | "keep"; hrNotes?: string },
): Promise<{ ok: boolean; status: string | null }> {
  return apiFetch(`/api/recruitment/applications/${applicationId}/hr-decision`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiSubmitInterviewFeedback(
  applicationId: number,
  interviewId: number,
  data: { outcome: "passed" | "failed" | "reviewed"; notes?: string },
): Promise<{ id: number; status: string; feedbackNotes: string | null }> {
  return apiFetch(`/api/recruitment/applications/${applicationId}/interviews/${interviewId}/feedback`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function apiDeleteRecruitmentInterview(
  applicationId: number,
  interviewId: number,
): Promise<{ success: boolean }> {
  return apiFetch(`/api/recruitment/applications/${applicationId}/interviews/${interviewId}`, {
    method: "DELETE",
  });
}

// Candidate Profiles
export async function apiGetCandidateProfile(appId: number): Promise<RecruitmentCandidateProfile | null> {
  return apiFetch(`/api/recruitment/applications/${appId}/profile`);
}
export async function apiUpsertCandidateProfile(
  appId: number,
  data: {
    skills?: string[];
    technicalKnowledge?: string | null;
    experienceSummary?: string | null;
    personalityNotes?: string | null;
    overallRating?: number | null;
    recommendation?: string | null;
  },
): Promise<RecruitmentCandidateProfile> {
  return apiFetch(`/api/recruitment/applications/${appId}/profile`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
export async function apiGetProfileComments(appId: number): Promise<RecruitmentProfileComment[]> {
  return apiFetch(`/api/recruitment/applications/${appId}/profile/comments`);
}
export async function apiAddProfileComment(appId: number, comment: string): Promise<RecruitmentProfileComment> {
  return apiFetch(`/api/recruitment/applications/${appId}/profile/comments`, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });
}
export async function apiDeleteProfileComment(appId: number, commentId: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/recruitment/applications/${appId}/profile/comments/${commentId}`, {
    method: "DELETE",
  });
}

// Interview Scorecards
export async function apiGetInterviewScorecard(appId: number, intId: number): Promise<RecruitmentInterviewScorecard | null> {
  return apiFetch(`/api/recruitment/applications/${appId}/interviews/${intId}/scorecard`);
}
export async function apiUpsertInterviewScorecard(
  appId: number,
  intId: number,
  data: {
    communication?: number | null;
    technical?: number | null;
    problemSolving?: number | null;
    cultureFit?: number | null;
    motivation?: number | null;
    strengths?: string | null;
    weaknesses?: string | null;
    overallRecommendation?: string | null;
  },
): Promise<RecruitmentInterviewScorecard> {
  return apiFetch(`/api/recruitment/applications/${appId}/interviews/${intId}/scorecard`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// Convert to Employee
export async function apiConvertApplicantToEmployee(
  appId: number,
  data: { departmentId?: number; jobTitle?: string; startDate?: string; salary?: number },
): Promise<{ employeeId: number; userId: number }> {
  return apiFetch(`/api/recruitment/applications/${appId}/convert-to-employee`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Email templates
export async function apiGetRecruitmentEmailTemplates(): Promise<RecruitmentEmailTemplate[]> {
  return apiFetch("/api/recruitment/email-templates");
}
export async function apiCreateRecruitmentEmailTemplate(data: {
  name: string;
  subject: string;
  bodyHtml: string;
  triggerStatus?: string | null;
}): Promise<RecruitmentEmailTemplate> {
  return apiFetch("/api/recruitment/email-templates", { method: "POST", body: JSON.stringify(data) });
}
export async function apiUpdateRecruitmentEmailTemplate(
  id: number,
  data: Partial<{ name: string; subject: string; bodyHtml: string; triggerStatus: string | null }>,
): Promise<RecruitmentEmailTemplate> {
  return apiFetch(`/api/recruitment/email-templates/${id}`, { method: "PUT", body: JSON.stringify(data) });
}
export async function apiDeleteRecruitmentEmailTemplate(id: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/recruitment/email-templates/${id}`, { method: "DELETE" });
}

// Public API (no auth)
export async function apiGetPublicRecruitmentPosition(slug: string): Promise<RecruitmentPublicPosition> {
  return apiFetchPublic(`/api/recruitment/public/positions/${slug}`);
}
export async function apiSubmitRecruitmentApplication(
  slug: string,
  formData: FormData,
): Promise<{ applicationId: number }> {
  const tenantSlug = getTenantSlug();
  const res = await fetch(
    `${API_BASE}/api/recruitment/public/positions/${slug}/apply?_t=${encodeURIComponent(tenantSlug)}`,
    {
      method: "POST",
      headers: { "x-tenant": tenantSlug },
      body: formData,
      cache: "no-store",
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

export async function apiDownloadRecruitmentResume(applicationId: number): Promise<void> {
  return apiDownloadBlob(
    `/api/recruitment/applications/${applicationId}/resume`,
    `candidate-${applicationId}-CV.pdf`,
  );
}

// ============================================================
// Analytics
// ============================================================

export interface AnalyticsDateParams {
  from?: string;
  to?: string;
}

export interface HeadcountData {
  total: number;
  active: number;
  onLeave: number;
  newHiresThisMonth: number;
  avgTenureMonths: number;
  monthly: { month: string; headcount: number }[];
  byDepartment: { department: string; count: number }[];
  byContractType: { contractType: string; count: number }[];
  byStatus: { status: string; count: number }[];
  byGender: { gender: string; count: number }[];
}

export interface TurnoverData {
  monthly: { month: string; hires: number; terminations: number; turnoverRate: number }[];
  totalHires: number;
  totalTerminations: number;
  avgTurnoverRate: number;
}

export interface LeaveSummaryData {
  byMonth: Record<string, Record<string, number>>;
  byDepartment: Record<string, Record<string, number>>;
  leaveTypes: string[];
}

export interface PayrollSummaryData {
  monthly: {
    period: string;
    totalGross: number;
    totalNet: number;
    totalContributions: number;
    totalTax: number;
    headcount: number;
  }[];
}

export interface DepartmentComparisonData {
  departments: {
    department: string;
    headcount: number;
    activeCount: number;
    avgTenureMonths: number;
    totalLeaveDays: number;
    avgGross: number;
  }[];
}

export interface PerformanceOverviewData {
  companyAvgRating: number;
  totalReviews: number;
  goalStats: {
    completed: number;
    inProgress: number;
    notStarted: number;
    overdue: number;
    total: number;
  };
  byDepartment: { department: string; avgRating: number; reviewCount: number }[];
}

function buildAnalyticsParams(params?: AnalyticsDateParams): string {
  const q = new URLSearchParams();
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  const qs = q.toString();
  return qs ? `?${qs}` : "";
}

export async function apiGetAnalyticsHeadcount(params?: AnalyticsDateParams): Promise<HeadcountData> {
  return apiFetch(`/api/analytics/headcount${buildAnalyticsParams(params)}`);
}

export async function apiGetAnalyticsTurnover(params?: AnalyticsDateParams): Promise<TurnoverData> {
  return apiFetch(`/api/analytics/turnover${buildAnalyticsParams(params)}`);
}

export async function apiGetAnalyticsLeaveSummary(params?: AnalyticsDateParams): Promise<LeaveSummaryData> {
  return apiFetch(`/api/analytics/leave-summary${buildAnalyticsParams(params)}`);
}

export async function apiGetAnalyticsPayrollSummary(params?: AnalyticsDateParams): Promise<PayrollSummaryData> {
  return apiFetch(`/api/analytics/payroll-summary${buildAnalyticsParams(params)}`);
}

export async function apiGetAnalyticsDepartmentComparison(): Promise<DepartmentComparisonData> {
  return apiFetch("/api/analytics/department-comparison");
}

export async function apiGetAnalyticsPerformanceOverview(): Promise<PerformanceOverviewData> {
  return apiFetch("/api/analytics/performance-overview");
}

export async function apiGetAnalyticsPivotDataset(): Promise<FlatEmployeeRow[]> {
  return apiFetch("/api/analytics/pivot-dataset");
}

// ── Saved analytics (pivot) reports ─────────────────────────────────────────
export interface SavedReportMeta {
  id: number;
  name: string;
  updatedAt: string;
}
export interface SavedReportFull {
  id: number;
  name: string;
  config: any;
}

export async function apiListAnalyticsReports(): Promise<SavedReportMeta[]> {
  return apiFetch("/api/analytics/reports");
}

export async function apiGetAnalyticsReport(id: number): Promise<SavedReportFull> {
  return apiFetch(`/api/analytics/reports/${id}`);
}

export async function apiCreateAnalyticsReport(name: string, config: any): Promise<SavedReportFull> {
  return apiFetch("/api/analytics/reports", {
    method: "POST",
    body: JSON.stringify({ name, config }),
  });
}

export async function apiUpdateAnalyticsReport(id: number, name: string, config: any): Promise<SavedReportFull> {
  return apiFetch(`/api/analytics/reports/${id}`, {
    method: "PUT",
    body: JSON.stringify({ name, config }),
  });
}

export async function apiDeleteAnalyticsReport(id: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/analytics/reports/${id}`, { method: "DELETE" });
}

// ── AI Assistant ──────────────────────────────────────────────────────────────

export interface AssistantMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AssistantUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
}

export interface AssistantResponse {
  message: string;
  actionsTaken: string[];
  usage: AssistantUsage;
}

export async function apiSendAssistantMessage(
  messages: AssistantMessage[],
  sessionId: string,
): Promise<AssistantResponse> {
  return apiFetch("/api/ai-assistant/chat", {
    method: "POST",
    body: JSON.stringify({ messages, sessionId }),
  });
}

export async function apiGetAssistantUsage(): Promise<{ totalPrompts: number; totalTokens: number }> {
  return apiFetch("/api/ai-assistant/usage");
}

// ════════════════════════════════════════════════════════════════════
// EXPENSE MANAGEMENT
// ════════════════════════════════════════════════════════════════════

export interface ExpenseAttachment {
  id: number;
  expenseId: number;
  fileKey: string;
  fileName: string;
  mimeType: string | null;
  uploadedAt: string;
}

export interface ExpensePayment {
  paidAmount: number;
  paymentDate: string;
  paymentMethod: string | null;
  notes: string | null;
  createdAt: string;
  reimbursedByName: string | null;
}

export interface Expense {
  id: number;
  employeeId: number;
  employeeName?: string | null;
  title: string;
  description: string | null;
  amount: number;
  currency: string;
  taxAmount: number;
  taxType: string | null;
  expenseDate: string;
  category: string;
  paidBy: string;
  status: "draft" | "submitted" | "pending_approval" | "approved" | "rejected" | "reimbursed";
  workflowInstanceId: number | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  attachments?: ExpenseAttachment[];
  // Approval / rejection tracking (populated on GET /:id)
  approvedBy?: number | null;
  approvedAt?: string | null;
  approvedByName?: string | null;
  rejectedBy?: number | null;
  rejectedAt?: string | null;
  rejectedByName?: string | null;
  payment?: ExpensePayment | null;
}

export interface ExpenseAnalytics {
  monthly: { month: string; total: number; count: number }[];
  byCategory: { category: string; total: number; count: number }[];
  byDepartment: { department: string; total: number; count: number }[];
  byTeam: { teamName: string; total: number; count: number }[];
  topSpenders: { employeeName: string; total: number; count: number }[];
  pendingApproval: { count: number; total: number };
  reimbursementBacklog: { count: number; total: number };
  totalTax: number;
}

export async function apiGetExpenses(params?: {
  status?: string;
  employeeId?: number;
  category?: string;
  from?: string;
  to?: string;
}): Promise<Expense[]> {
  const q = params ? "?" + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)])).toString() : "";
  return apiFetch(`/api/expenses${q}`);
}

export async function apiCreateExpense(data: {
  title: string;
  description?: string;
  amount: number;
  currency?: string;
  taxAmount?: number;
  taxType?: string;
  expenseDate: string;
  category: string;
  paidBy?: string;
  employeeId?: number;
}): Promise<Expense> {
  return apiFetch("/api/expenses", { method: "POST", body: JSON.stringify(data) });
}

export async function apiGetExpense(id: number): Promise<Expense> {
  return apiFetch(`/api/expenses/${id}`);
}

export async function apiUpdateExpense(id: number, data: Partial<{
  title: string;
  description: string;
  amount: number;
  currency: string;
  taxAmount: number;
  taxType: string;
  expenseDate: string;
  category: string;
  paidBy: string;
}>): Promise<Expense> {
  return apiFetch(`/api/expenses/${id}`, { method: "PATCH", body: JSON.stringify(data) });
}

export async function apiDeleteExpense(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`/api/expenses/${id}`, { method: "DELETE" });
}

export async function apiSubmitExpense(id: number): Promise<Expense> {
  return apiFetch(`/api/expenses/${id}/submit`, { method: "POST" });
}

export async function apiApproveExpense(id: number): Promise<Expense> {
  return apiFetch(`/api/expenses/${id}/approve`, { method: "POST" });
}

export async function apiRejectExpense(id: number, reason: string): Promise<Expense> {
  return apiFetch(`/api/expenses/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
}

export async function apiReimburseExpense(id: number, data?: {
  paymentDate?: string;
  paymentMethod?: string;
  notes?: string;
}): Promise<Expense> {
  return apiFetch(`/api/expenses/${id}/reimburse`, { method: "POST", body: JSON.stringify(data ?? {}) });
}

export async function apiUploadExpenseReceipt(expenseId: number, file: File): Promise<ExpenseAttachment> {
  const formData = new FormData();
  formData.append("file", file);
  return apiMultipartFetch<ExpenseAttachment>(`/api/expenses/${expenseId}/receipts`, formData);
}

export async function apiDeleteExpenseReceipt(expenseId: number, attachId: number): Promise<{ ok: boolean }> {
  return apiFetch(`/api/expenses/${expenseId}/receipts/${attachId}`, { method: "DELETE" });
}

export function getExpenseReceiptUrl(expenseId: number, attachId: number): string {
  const params = buildReceiptFileParams();
  return `${API_BASE}/api/expenses/${expenseId}/receipts/${attachId}/file?${params.toString()}`;
}

export async function apiGetExpenseAnalytics(params?: {
  from?: string;
  to?: string;
  department?: string;
  team?: string;
  amountMin?: string;
  amountMax?: string;
  category?: string;
  paidBy?: string;
}): Promise<ExpenseAnalytics> {
  const q = params ? "?" + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== "") as [string, string][]).toString() : "";
  return apiFetch(`/api/expenses/analytics/summary${q}`);
}

// ── Cash Advances ─────────────────────────────────────────────────────────────

export interface CashAdvanceReceipt {
  id: number;
  cashAdvanceId: number;
  fileKey: string;
  fileName: string;
  mimeType: string | null;
  uploadedAt: string;
}

export interface CashAdvance {
  id: number;
  employeeId: number;
  employeeName?: string | null;
  amount: number;
  currency: string;
  purpose: string;
  status:
    | "pending_lm"
    | "pending_hr"
    | "approved"
    | "report_submitted"
    | "report_pending_hr"
    | "report_rejected"
    | "settled"
    | "rejected";
  rejectionReason: string | null;
  lmApprovedAt: string | null;
  lmApprovedBy: number | null;
  hrApprovedAt: string | null;
  hrApprovedBy: number | null;
  reportAmount: number | null;
  reportNotes: string | null;
  reportSubmittedAt: string | null;
  reportLmApprovedAt: string | null;
  reportLmApprovedBy: number | null;
  settlementType: "none" | "payout" | "deduction" | null;
  settlementAmount: number | null;
  settlementNotes: string | null;
  settledAt: string | null;
  settledBy: number | null;
  createdAt: string;
  updatedAt: string;
  receipts?: CashAdvanceReceipt[];
}

export async function apiGetCashAdvances(params?: {
  status?: string;
  employeeId?: number;
}): Promise<CashAdvance[]> {
  const q = params
    ? "?" +
      new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : "";
  return apiFetch(`/api/expenses/cash-advances${q}`);
}

export async function apiCreateCashAdvance(data: {
  amount: number;
  currency?: string;
  purpose: string;
}): Promise<CashAdvance> {
  return apiFetch("/api/expenses/cash-advances", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiGetCashAdvance(id: number): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}`);
}

export async function apiCancelCashAdvance(id: number): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/cancel`, { method: "POST" });
}

export async function apiApproveCashAdvanceLM(id: number): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/approve-lm`, { method: "POST" });
}

export async function apiRejectCashAdvanceLM(id: number, reason: string): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/reject-lm`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function apiApproveCashAdvanceHR(id: number): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/approve-hr`, { method: "POST" });
}

export async function apiRejectCashAdvanceHR(id: number, reason: string): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/reject-hr`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function apiSubmitCashAdvanceReport(
  id: number,
  data: { reportAmount: number; reportNotes?: string },
): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/submit-report`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiApproveCashAdvanceReport(id: number): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/approve-report-lm`, { method: "POST" });
}

export async function apiRejectCashAdvanceReport(id: number, reason: string): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/reject-report-lm`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function apiSettleCashAdvance(
  id: number,
  data?: { settlementNotes?: string },
): Promise<CashAdvance> {
  return apiFetch(`/api/expenses/cash-advances/${id}/settle`, {
    method: "POST",
    body: JSON.stringify(data ?? {}),
  });
}

export async function apiUploadCashAdvanceReceipt(
  id: number,
  file: File,
): Promise<CashAdvanceReceipt> {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetch(`/api/expenses/cash-advances/${id}/receipts`, {
    method: "POST",
    body: formData,
  });
}

export async function apiDeleteCashAdvanceReceipt(
  id: number,
  attachId: number,
): Promise<{ ok: boolean }> {
  return apiFetch(`/api/expenses/cash-advances/${id}/receipts/${attachId}`, { method: "DELETE" });
}

export function getCashAdvanceReceiptUrl(id: number, attachId: number): string {
  const params = buildReceiptFileParams();
  return `${API_BASE}/api/expenses/cash-advances/${id}/receipts/${attachId}/file?${params.toString()}`;
}

// Short-lived, file-scoped token (typ:"file") used to load private receipt files
// via <img src>/<a href>. We deliberately do NOT put the long-lived session JWT
// in the URL — it would leak via logs/Referer. Call apiRefreshFileToken() (e.g.
// from a page that renders receipts) to keep this fresh; it expires server-side
// in 10 minutes.
let fileAccessToken: string | null = null;

export async function apiRefreshFileToken(): Promise<void> {
  try {
    const r = await apiFetch<{ token: string }>("/api/auth/file-token");
    fileAccessToken = r.token;
  } catch {
    fileAccessToken = null;
  }
}

// ── Web Push (PWA notifications) ─────────────────────────────────────────────
export async function apiGetVapidPublicKey(): Promise<{ key: string | null; enabled: boolean }> {
  return apiFetch("/api/push/vapid-public-key");
}
export async function apiSubscribePush(sub: unknown): Promise<{ ok: boolean }> {
  return apiFetch("/api/push/subscribe", { method: "POST", body: JSON.stringify(sub) });
}
export async function apiUnsubscribePush(endpoint: string): Promise<{ ok: boolean }> {
  return apiFetch("/api/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint }) });
}

function buildReceiptFileParams(): URLSearchParams {
  const params = new URLSearchParams();
  const userId = getCurrentUserId();

  params.set("_t", getTenantSlug());
  if (userId) params.set("uid", userId);
  if (fileAccessToken) params.set("token", fileAccessToken);

  return params;
}

// ── Calendar integration ──────────────────────────────────────────────────────

export interface CalendarStatus {
  connected: boolean;
  provider: "microsoft" | "google" | null;
  connectedAt: string | null;
}

export interface FreeSlot {
  date: string;
  startsAt: string;
  endsAt: string;
}

export async function apiGetCalendarStatus(): Promise<CalendarStatus> {
  return apiFetch("/api/calendar/oauth/status");
}

export async function apiDisconnectCalendar(): Promise<{ success: boolean }> {
  return apiFetch("/api/calendar/oauth/disconnect", { method: "DELETE" });
}

export function getCalendarOAuthUrl(provider: "microsoft" | "google", returnPath = "/settings?tab=calendar"): string {
  const tenant = getTenantSlug();
  const userId = getCurrentUserId();
  const origin = encodeURIComponent(window.location.origin);
  return `${API_BASE}/api/calendar/oauth/start?provider=${provider}&returnPath=${encodeURIComponent(returnPath)}&origin=${origin}&_t=${encodeURIComponent(tenant)}&uid=${userId ?? ""}`;
}

export async function apiCreateCalendarMeeting(data: {
  title: string;
  startTime: string; // ISO string
  endTime: string;   // ISO string
}): Promise<{ meetLink: string; provider: "microsoft" | "google" }> {
  return apiFetch("/api/calendar/create-meeting", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function apiGetFreeSlots(
  start: string,
  end: string,
  slotDuration: number,
  timezone: string,
): Promise<{ slots: FreeSlot[]; provider: string }> {
  return apiFetch(
    `/api/calendar/free-busy?start=${start}&end=${end}&slotDuration=${slotDuration}&timezone=${encodeURIComponent(timezone)}`,
  );
}

export async function apiSetInterviewAvailability(
  appId: number,
  intId: number,
  data: { windows: Array<{ date: string; startTime: string; endTime: string }>; slotDurationMinutes: number; timezone: string },
): Promise<{ success: boolean; totalSlots: number; bookingUrl: string; bookingToken: string }> {
  return apiFetch(`/api/recruitment/applications/${appId}/interviews/${intId}/availability`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface PublicSchedulingPage {
  alreadyBooked?: boolean;
  scheduledAt?: string;
  interview?: {
    id: number;
    positionTitle: string;
    interviewerName: string | null;
    companyName: string | null;
    candidateName: string;
    slotDurationMinutes: number;
    timezone: string;
  };
  slots?: Array<{ id: number; startsAt: string; endsAt: string }>;
}

export async function apiGetPublicSchedulingPage(tenantSlug: string, token: string): Promise<PublicSchedulingPage> {
  const res = await fetch(`${API_BASE}/api/recruitment/public/scheduling/${tenantSlug}/${token}`, {
    headers: { "x-tenant": tenantSlug },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Error ${res.status}`);
  }
  return res.json();
}

export async function apiBookInterviewSlot(
  tenantSlug: string,
  token: string,
  slotId: number,
): Promise<{ confirmedSlot: { startsAt: string; endsAt: string }; message: string }> {
  const res = await fetch(`${API_BASE}/api/recruitment/public/scheduling/${tenantSlug}/${token}/book`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-tenant": tenantSlug },
    body: JSON.stringify({ slotId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Error ${res.status}`);
  }
  return res.json();
}

// ── SSO / SAML ────────────────────────────────────────────────────────────────

export interface SsoConfig {
  id: number;
  enabled: boolean;
  federation_metadata_url: string;
  // Auto-populated from federation metadata — read-only from the UI
  idp_entity_id: string;
  idp_sso_url: string;
  idp_certificate: string;
  // Legacy fields — kept for DB backward compatibility, not used by UI
  sp_entity_id?: string;
  attribute_email?: string;
  attribute_firstname?: string;
  attribute_lastname?: string;
}

export async function apiGetSsoStatus(): Promise<{ enabled: boolean }> {
  const tenantSlug = getTenantSlug();
  const res = await fetch(`${API_BASE}/api/sso/status`, {
    headers: { "x-tenant": tenantSlug },
  });
  if (!res.ok) return { enabled: false };
  return res.json();
}

export async function apiGetSsoConfig(): Promise<SsoConfig> {
  return apiFetch("/api/sso/config");
}

export async function apiSaveSsoConfig(config: Partial<SsoConfig>): Promise<{ ok: boolean }> {
  return apiFetch("/api/sso/config", {
    method: "PUT",
    body: JSON.stringify(config),
  });
}

export async function apiGetMe(token: string, tenantSlug: string): Promise<{ user: any }> {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-tenant": tenantSlug,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) throw new Error("Failed to fetch user");
  return res.json();
}

// ── GDPR retention (HR/Admin only) ──────────────────────────────────────────

export interface GdprPendingApplication {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  applied_at: string;
  gdpr_expiry: string;
  position_title: string | null;
  expired: boolean;
}

export interface GdprPendingDocument {
  id: number;
  employee_id: number;
  document_name: string;
  document_type: string | null;
  gdpr_expiry: string;
  expired: boolean;
  first_name: string | null;
  last_name: string | null;
}

export interface GdprPending {
  candidateApplications: GdprPendingApplication[];
  documents: GdprPendingDocument[];
}

export interface GdprHistoryEntry {
  id: number;
  actor: string;
  actorIsCron: boolean;
  message: string;
  kind: "candidate" | "document" | "other";
  createdAt: string;
}

export interface GdprHistory {
  rows: GdprHistoryEntry[];
  stats: { lastDay: number; lastWeek: number; lastMonth: number; total: number };
}

export async function apiGetGdprPending(): Promise<GdprPending> {
  return apiFetch<GdprPending>("/api/gdpr/pending-retention");
}

export async function apiGetGdprHistory(limit = 100): Promise<GdprHistory> {
  return apiFetch<GdprHistory>(`/api/gdpr/retention-history?limit=${limit}`);
}

export async function apiGdprExtendRetention(
  entityType: "candidate_application" | "document",
  entityId: number,
  days: number,
): Promise<{ ok: boolean }> {
  return apiFetch("/api/gdpr/extend-retention", {
    method: "POST",
    body: JSON.stringify({ entityType, entityId, days }),
  });
}

export async function apiGdprDeleteNow(
  entityType: "candidate_application" | "document",
  entityId: number,
): Promise<{ ok: boolean; filesDeleted: number }> {
  return apiFetch("/api/gdpr/delete-now", {
    method: "POST",
    body: JSON.stringify({ entityType, entityId }),
  });
}

// ════════════════════════════════════════════════════════════════════
// EXTENDED LEAVE FRAMEWORK
// ════════════════════════════════════════════════════════════════════

export type EmployeeAccessMode = "full" | "limited" | "disabled";
export type EmployeeLifecycleStatus =
  | "Active"
  | "On Leave"
  | "Inactive"
  | "Left the Company"
  | "Disabled";

export interface ExtendedLeaveTenantSettings {
  leaveType: string;
  enabled: boolean;
  useCountryDefaults: boolean;
  customDurationDays: number | null;
  allowManualOverride: boolean;
  requiresHrApproval: boolean;
  maxDaysPerRequest: number | null;
  paidLeave: boolean;
  autoReactivateEmployee: boolean;
  returnReminderDaysBefore: number;
}

export interface ExtendedLeaveCountryPolicy {
  country: string;
  leaveType: string;
  defaultDurationDays: number | null;
  allowManualOverride: boolean;
  isFixedDuration: boolean;
}

export interface ResolvedExtendedLeavePolicy {
  leaveType: string;
  country: string;
  /** Effective duration in calendar days */
  durationDays: number | null;
  /** Where the duration came from */
  source: "tenant_override" | "country_default" | "not_configured";
  /** Whether HR is permitted to override the end date */
  allowManualOverride: boolean;
  tenantSettings: ExtendedLeaveTenantSettings | null;
  countryPolicy: ExtendedLeaveCountryPolicy | null;
}

/**
 * Fetch all extended leave type tenant settings (HR/Admin only).
 */
export async function apiGetExtendedLeavePolicies(): Promise<{ settings: ExtendedLeaveTenantSettings[] }> {
  return apiFetch("/api/leave/extended-policy");
}

/**
 * Fetch the resolved policy for a specific leave type.
 * Optionally pass employeeId to derive the country from the employee's location.
 */
export async function apiGetExtendedLeavePolicy(
  leaveType: string,
  opts?: { employeeId?: number; country?: string },
): Promise<ResolvedExtendedLeavePolicy> {
  const q = new URLSearchParams();
  if (opts?.employeeId) q.set("employeeId", String(opts.employeeId));
  if (opts?.country) q.set("country", opts.country);
  const qs = q.toString() ? `?${q}` : "";
  return apiFetch(`/api/leave/extended-policy/${encodeURIComponent(leaveType)}${qs}`);
}

/**
 * Update tenant-level settings for an extended leave type (HR/Admin only).
 */
export async function apiUpdateExtendedLeavePolicy(
  leaveType: string,
  settings: Partial<Omit<ExtendedLeaveTenantSettings, "leaveType">>,
): Promise<{ ok: boolean; settings: ExtendedLeaveTenantSettings }> {
  return apiFetch(`/api/leave/extended-policy/${encodeURIComponent(leaveType)}`, {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

/**
 * Manually reactivate an employee after extended leave (HR/Admin only).
 * @param leaveRequestId - The ID of the approved extended leave request
 */
export async function apiReactivateEmployee(leaveRequestId: number): Promise<{
  ok: boolean;
  employeeId: number;
  status: EmployeeLifecycleStatus;
  accessMode: EmployeeAccessMode;
}> {
  return apiFetch(`/api/leave/requests/${leaveRequestId}/reactivate`, {
    method: "POST",
  });
}


// ── Pre-boarding (public, no auth header) ─────────────────────────────────────

export interface PreboardingCorporateDoc {
  id: number;
  documentName: string;
  fileUrl: string;
  confirmedAt: string | null;
  sortOrder: number;
}

export interface PreboardingContext {
  firstName: string;
  lastName: string;
  positionTitle: string | null;
  companyName: string | null;
  logoUrl: string | null;
  companyCountry: string | null;
  isCompleted: boolean;
  corporateDocs: PreboardingCorporateDoc[];
}

export interface PreboardingColleague {
  name: string;
  jobTitle: string | null;
  avatarUrl: string | null;
  avatarColor: string;
  initials: string;
}

function preboardingBase(tenantSlug: string, token: string): string {
  return `${API_BASE}/api/recruitment/public/preboarding/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(token)}`;
}

export async function apiGetPreboardingContext(tenantSlug: string, token: string): Promise<PreboardingContext> {
  const res = await fetch(preboardingBase(tenantSlug, token));
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

export async function apiPreboardingUploadAvatar(tenantSlug: string, token: string, file: File): Promise<{ avatarUrl: string }> {
  const fd = new FormData();
  fd.append("avatar", file);
  const res = await fetch(`${preboardingBase(tenantSlug, token)}/avatar`, { method: "POST", body: fd });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

export async function apiPreboardingPersonalDetails(
  tenantSlug: string,
  token: string,
  data: {
    phone?: string;
    address?: string;
    nationality?: string;
    birthday?: string;
    bankDetails?: { bankName?: string; iban?: string; bicSwift?: string; accountHolderName?: string };
    emergencyContact?: { contactName?: string; relationship?: string; phone?: string; email?: string };
    children?: Array<{ firstName: string; dateOfBirth?: string }>;
  },
): Promise<{ success: boolean }> {
  const res = await fetch(`${preboardingBase(tenantSlug, token)}/personal-details`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

export async function apiPreboardingUploadDocument(
  tenantSlug: string,
  token: string,
  type: "id_card" | "bank_card",
  file: File,
): Promise<{ documentId: number; fileUrl: string }> {
  const fd = new FormData();
  fd.append("type", type);
  fd.append("file", file);
  const res = await fetch(`${preboardingBase(tenantSlug, token)}/documents`, { method: "POST", body: fd });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

export async function apiPreboardingConfirmCorporateDoc(tenantSlug: string, token: string, docId: number): Promise<void> {
  const res = await fetch(`${preboardingBase(tenantSlug, token)}/corporate-docs/${docId}/confirm`, { method: "POST" });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
}

export async function apiPreboardingColleagues(tenantSlug: string, token: string): Promise<PreboardingColleague[]> {
  const res = await fetch(`${preboardingBase(tenantSlug, token)}/colleagues`);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

export async function apiPreboardingComplete(tenantSlug: string, token: string): Promise<void> {
  const res = await fetch(`${preboardingBase(tenantSlug, token)}/complete`, { method: "POST" });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
}

export async function apiSendPreboarding(
  applicationId: number,
  docs: Array<{ name: string; file: File }>,
): Promise<{ sent: boolean; expiresAt: string }> {
  const fd = new FormData();
  docs.forEach((d) => {
    fd.append("file", d.file, d.file.name);
    fd.append("docNames", d.name);
  });
  const token = getStoredToken();
  const tenantSlug = getTenantSlug();
  const headers: Record<string, string> = {
    "x-tenant": tenantSlug,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const sep = `/api/recruitment/applications/${applicationId}/send-preboarding`.includes("?") ? "&" : "?";
  const res = await fetch(
    `${API_BASE}/api/recruitment/applications/${applicationId}/send-preboarding${sep}_t=${encodeURIComponent(tenantSlug)}`,
    { method: "POST", headers, body: fd, cache: "no-store" },
  );
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

// ════════════════════════════════════════════════════════════════════════════
// Timekeeping
// ════════════════════════════════════════════════════════════════════════════
export type TimesheetStatus =
  | "Draft" | "Submitted" | "Approved" | "Rejected" | "Needs Changes" | "Locked";

export interface TimekeepingTask {
  id: number;
  name: string;
  description: string | null;
  color: string | null;
  project_code: string | null;
  is_active: boolean;
  billable: boolean;
}

export interface TimesheetEntry {
  id: number;
  timesheet_id: number;
  entry_date: string;
  task_id: number | null;
  task_name?: string | null;
  task_color?: string | null;
  hours: number;
  start_time: string | null;
  end_time: string | null;
  break_minutes: number;
  notes: string | null;
}

export interface TimesheetDay {
  date: string;
  entries: TimesheetEntry[];
  leave: { type: string } | null;
  holiday?: boolean;
  /** Day outside the employee's employment window (before hire / after last day). */
  outOfEmployment?: boolean;
}

export interface TimesheetWeek {
  employeeId: number;
  weekStartDate: string;
  weekEndDate: string;
  status: TimesheetStatus;
  timesheetId: number | null;
  requiredHours: number;
  /** JavaScript weekday numbers (0=Sun ... 6=Sat) from the employee's schedule. */
  workingWeekdays: number[];
  /** Employee schedule before leave/holiday adjustments; the baseline remains requiredHours. */
  scheduledRequiredHours: number;
  /** Expected worked hours after approved leave and public holidays. */
  effectiveRequiredHours: number;
  leaveHours: number;
  holidayHours: number;
  leaveDays: number;
  holidayDays: number;
  totalHours: number;
  employmentStart?: string | null;
  employmentEnd?: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectReason: string | null;
  returnReason: string | null;
  days: TimesheetDay[];
  warnings: string[];
}

export interface TimekeepingSettingsDTO {
  defaultRequiredHours: number;
  allowFutureDates: boolean;
  copyNotesDefault: boolean;
  weekStartsOn: number;
  maxDailyHours: number;
}

export interface TimesheetApprovalRow {
  id: number;
  employee_id: number;
  employee_name: string;
  department: string | null;
  week_start_date: string;
  status: TimesheetStatus;
  total_hours: number;
  required_hours: number;
  scheduledRequiredHours: number;
  effectiveRequiredHours: number;
  leaveHours: number;
  holidayHours: number;
  leaveDays: number;
  holidayDays: number;
  submitted_at: string | null;
}

export interface TimesheetEntryInput {
  entryDate: string;
  taskId?: number | null;
  hours: number;
  startTime?: string | null;
  endTime?: string | null;
  breakMinutes?: number | null;
  notes?: string | null;
}

export async function apiGetTimekeepingTasks(includeInactive = false): Promise<TimekeepingTask[]> {
  return apiFetch(`/api/timekeeping/tasks${includeInactive ? "?includeInactive=1" : ""}`);
}
export async function apiCreateTimekeepingTask(data: {
  name: string; description?: string; color?: string; projectCode?: string; billable?: boolean;
}): Promise<TimekeepingTask> {
  return apiFetch("/api/timekeeping/tasks", { method: "POST", body: JSON.stringify(data) });
}
export async function apiUpdateTimekeepingTask(id: number, data: Partial<{
  name: string; description: string; color: string; projectCode: string; isActive: boolean; billable: boolean;
}>): Promise<TimekeepingTask> {
  return apiFetch(`/api/timekeeping/tasks/${id}`, { method: "PATCH", body: JSON.stringify(data) });
}
export async function apiDeleteTimekeepingTask(id: number): Promise<{ success: boolean }> {
  return apiFetch(`/api/timekeeping/tasks/${id}`, { method: "DELETE" });
}

export async function apiGetTimesheetWeek(date: string, employeeId?: number): Promise<TimesheetWeek> {
  const qs = new URLSearchParams({ date });
  if (employeeId) qs.set("employeeId", String(employeeId));
  return apiFetch(`/api/timekeeping/week?${qs.toString()}`);
}
export async function apiEnsureTimesheetWeek(date: string): Promise<TimesheetWeek> {
  return apiFetch("/api/timekeeping/week", { method: "POST", body: JSON.stringify({ date }) });
}
export async function apiSaveTimesheetEntries(timesheetId: number, entries: TimesheetEntryInput[]): Promise<TimesheetWeek> {
  return apiFetch(`/api/timekeeping/week/${timesheetId}/entries`, { method: "PUT", body: JSON.stringify({ entries }) });
}
export async function apiSubmitTimesheet(timesheetId: number): Promise<TimesheetWeek> {
  return apiFetch(`/api/timekeeping/week/${timesheetId}/submit`, { method: "POST", body: JSON.stringify({}) });
}
export async function apiCopyPreviousWeek(timesheetId: number, opts: {
  mode: "all" | "days"; days?: number[]; includeNotes?: boolean;
}): Promise<TimesheetWeek> {
  return apiFetch(`/api/timekeeping/week/${timesheetId}/copy-previous`, { method: "POST", body: JSON.stringify(opts) });
}

export async function apiGetTimesheetApprovals(): Promise<TimesheetApprovalRow[]> {
  return apiFetch("/api/timekeeping/approvals");
}

export interface TimekeepingEmployee {
  id: number;
  name: string;
  department: string | null;
}

/** Employees the caller may enter timecards for (HR/admin → all; line manager → direct reports). */
export async function apiGetTimekeepingEmployees(): Promise<TimekeepingEmployee[]> {
  return apiFetch("/api/timekeeping/admin/employees");
}

/** HR/admin or the employee's line manager enter/edit a week on behalf of an employee (works for closed weeks). */
export async function apiAdminSaveTimesheet(
  employeeId: number, weekStart: string, entries: TimesheetEntryInput[],
): Promise<TimesheetWeek> {
  return apiFetch("/api/timekeeping/admin/week", {
    method: "PUT",
    body: JSON.stringify({ employeeId, weekStart, entries }),
  });
}
export async function apiApproveTimesheet(timesheetId: number, notes?: string): Promise<TimesheetWeek> {
  return apiFetch(`/api/timekeeping/week/${timesheetId}/approve`, { method: "POST", body: JSON.stringify({ notes }) });
}
export async function apiRejectTimesheet(timesheetId: number, reason?: string): Promise<TimesheetWeek> {
  return apiFetch(`/api/timekeeping/week/${timesheetId}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
}
export async function apiReturnTimesheet(timesheetId: number, reason?: string): Promise<TimesheetWeek> {
  return apiFetch(`/api/timekeeping/week/${timesheetId}/return`, { method: "POST", body: JSON.stringify({ reason }) });
}

export async function apiGetTimekeepingSettings(): Promise<TimekeepingSettingsDTO> {
  return apiFetch("/api/timekeeping/settings");
}
export async function apiUpdateTimekeepingSettings(data: Partial<TimekeepingSettingsDTO>): Promise<TimekeepingSettingsDTO> {
  return apiFetch("/api/timekeeping/settings", { method: "PATCH", body: JSON.stringify(data) });
}

export interface TimekeepingReport {
  range: { from: string; to: string; department: string | null };
  workforce: { totalHours: number; employeesTracked: number; avgHoursPerEmployee: number; avgWeeklyHours: number };
  compliance: { totalSheets: number; submittedPct: number; approvedPct: number; rejectedCount: number; pendingCount: number };
  hoursByDepartment: { department: string; hours: number }[];
  hoursByTask: { task: string; hours: number }[];
  hoursByWeek: { week: string; hours: number }[];
  employees: {
    id: number; name: string; department: string; total_hours: number;
    weeks: number; approved_weeks: number; pending_weeks: number; rejected_weeks: number;
  }[];
}

export async function apiGetTimekeepingReport(params?: { from?: string; to?: string; department?: string }): Promise<TimekeepingReport> {
  const qs = new URLSearchParams();
  if (params?.from) qs.set("from", params.from);
  if (params?.to) qs.set("to", params.to);
  if (params?.department) qs.set("department", params.department);
  const s = qs.toString();
  return apiFetch(`/api/timekeeping/reports/summary${s ? `?${s}` : ""}`);
}
